'use client';

// Блоки страницы «Настройки» (объединена с бывшим «Аккаунтом», 2026-07-03):
// язык · тема · ИИ-ассистент (MCP). Разделы «Твой n8n / Ресурсы / Доступ» удалены.
import { SETTINGS_MCP_STAFF_HIDDEN, SETTINGS_PRODUCTS_PEOPLE_HIDDEN } from '@/lib/hidden-features';
import { usePaneVisible } from '@/lib/pane-visible';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { CopyBtn } from './copy-btn';
import { useLocale, useTranslations } from 'next-intl';
import { useLiveLocale } from './locale-live';
import { isAppClient, readLayoutMode, subscribeLayoutMode, readPhoneLook, useLayoutMode, usePhoneLook } from './layout-mode';
import { useSlidePanel } from './native-layer-host';
import { ConfirmInline } from './confirm-inline';
import { DEmpty, DGroup, DHint, DList } from './drawer-kit';
import { FBlock, FChoiceBlock, FError, FHint, FIn, FNotifyList, FPane, KInput, KSwitch } from './form-kit';
import { KBareButton, KButton, KListGroup, KListRow, KListSection } from '@scriptera/ui/kit';
// «Диагностика» (владелец 25.08.2026): журнал страницы уезжает нам одной кнопкой.
import { sendJournal, lastJournalError } from '@/lib/client-report';
import {
  BillingIcon,
  BillingPanel,
  PaymentsHistoryButton,
  PlanPanel,
  usePaymentsDrawer,
  usePlanFacts,
} from './billing';
import { OverdueDot, useOverdueBadge } from './billing-views';
import { useFmtDate } from './billing-shared';
import { Sk, useShapeRecord } from './skeleton';
import { PartnerPaneLead, PartnerPaneTitle, PartnerPanel, PeopleIcon } from './partner-panel';
import { usePersistentData } from '@/lib/use-persistent-data';
import { pickToPath, type UrlSection, pathToPick } from '@/lib/cabinet-urls';
import { useRouter } from '@/lib/cabinet-nav';
import {
  isNativeApp,
  nativeBridge,
  openHelpSheet,
  nativeMethod,
  openWebSheet,
  readRoleHex,
  useNativeApp,
  useNativeMethod,
  type NativeAccountField,
  type NativeAccountGroup,
  type NativeAccountHero,
  type NativeAccountPage,
} from '@/lib/native-app';
// Родная справка (владелец 10.09.2026): раздел рисует натив, данные шлёт этот водитель.
import { openNativeHelp, useNativeHelp } from '@/lib/help-native';
import {
  NATIVE_SETTINGS_OPEN_EVENT,
  NATIVE_SETTINGS_POP_EVENT,
  NATIVE_SETTINGS_PUSH_EVENT,
  NATIVE_SETTINGS_REPLACE_EVENT,
  accountRoleHex,
  accountRowIcon,
  nativeAccountSupported,
  openNativeSettingsPage,
  pushNativeSettingsPage,
  takePendingSettingsStack,
  useNativeAccount,
} from '@/lib/account-native';
import { PROD_ORIGIN, STAGING_ORIGIN } from '@/lib/our-accounts';
import { AVATAR_CHANGED_EVENT, avatarTabPng } from './avatar';
import { IconConnections, IconContacts, IconFolder, IconProducts } from './icons';
import { DataSectionInline, useOpenDataSection } from './data-menu';
// Родные страницы справочников (волна 2) собирают ИХ ЖЕ руки — рядом с самим разделом.
import { useNativeConnectionsPage } from './connections';
import { useNativeContactsPage } from './contacts';
import { CT_FOLDERS_PAGE } from './contact-folders';
import type { DialogAccount } from './messages';
import { BotsFoldersPanel, BT_ROW, useNativeBotsFoldersPages, BT_FOLDERS_PAGE, useOpenBotsFolders } from './bots-folders';
import { TagIcon, TagsPanel, TAGS_COLOR, useNativeTagsPages } from './tags-settings';
import { TAGS_PAGE_KEYS, TAGS_ROW } from '@/lib/contact-tags-page';
import { ChatFoldersInline, useFoldersSettings, useNativeChatFoldersPages } from './messages-folders';
import { ContactFoldersInline, useContactFoldersWeb } from './contact-folders';
import { askNativeConfirm, hasNativeConfirm, hasNativeNotice, showNativeNotice } from './confirm-sheet';
import { isStepUp, StepUpDialog } from './step-up-dialog';
import { pollTfaOff, TfaOffConfirmDialog, type TfaOffConfirm } from './tfa-off-confirm';
import { useLogout } from './logout-button';
import {
  CabinetSwitchPanel,
  CabinetsIcon,
  EmployeesIcon,
  EmployeesInline,
  useCabinets,
  useNativeCabinetsPage,
  useNativeEmployeesPage,
  useOpenEmployees,
} from './staff';
import { N8nIconImg, useIsDark } from './n8n-icon';
import { useNativeIcons, type NativeIconAsk } from './native-icon';
import { CoinsIcon, EarningsPanel } from './earnings';
import { SpendReportSheet, SpendShareButton, openSpendShareMenu } from './spend-report';
import {
  InboxTopButton,
  NATIVE_NOTIFY_PAGE_KEYS,
  NotificationsSheet,
  useInboxBadge,
  useNativeNotifyPage,
  useOpenInbox,
} from './notifications';
import { PeopleDataIcon, PeopleDataPanel } from './people-data';
import { PageAction, PageAddButton, PaneHead } from './page-action';
import {
  NATIVE_PROFILE_PAGE_KEYS,
  ProfileCard,
  ProfilePanel,
  useNativeProfilePage,
  useOpenProfile,
  type ProfileInfo,
} from './profile';
import { TimezoneField, useTimezoneSetting } from './timezone-field';
import { applyTopStrip } from '@/lib/page-theme-color';
import { GEO_COUNTRY_KEY, GeoFields, geoNativeField, useGeoSetting } from './geo-field';
import { useOpenPayer } from './payer-sheet';
import { zoneLabel } from '@/lib/timezones';
import { SettingsHero } from './settings-hero';
import { HelpPane, HelpPaneLead, useOpenHelpLayer } from './help-layer';

/** Ключ/адрес данных «Безопасности» — их же греет фоном прогрев настроек (settings-warm.ts). */
export const SECURITY_KEY = 'cabinet:security';
export const SECURITY_URL = '/api/user/security';

// ─────────────────────────── Строки-разделы ───────────────────────────
// Своей строки-раздела (SettingRow: плитка 48, свой шеврон, свой пузырь .rows set-rows)
// у страниц БОЛЬШЕ НЕТ (владелец 12.08.2026, после страницы «Уведомления»: «размер
// иконок-квадратиков и вид пузырей намного красивее — перенеси на Аккаунт, Профиль,
// Безопасность»). Списки разделов собирает НАБОР: FPane + FNotifyList (пузырь радиуса 26
// с тенью пузыря, плитка значка 34/11, шеврон и сводка — от компонента).

// Иконки строк (белые на цветном фоне .ic).
const iconProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const McpIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}><path d="M12 3l2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5z" /></svg>
);
// Кнопки-значка «солнце/луна» и «RU/EN» справа в строке БОЛЬШЕ НЕТ (12.08.2026): строка
// списка теперь собирается набором (FNotifyList), своего контрола справа у неё не бывает
// — текущее значение стоит серым под именем, а тап по строке переключает.
const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9s1.3-6.4 3.8-9z" /></svg>
);
// Значок пузыря «Папки» (П85) — контурная папка, общий рисунок кабинета (icons.tsx).
const FolderIcon = () => <IconFolder />;
const PaletteIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}><path d="M12 3a9 9 0 1 0 0 18h1.5a2.5 2.5 0 0 0 0-5H12a2 2 0 0 1 0-4h6.5A2.5 2.5 0 0 0 21 9.5 8.7 8.7 0 0 0 12 3z" /><path d="M7.5 10.5h.01M10.5 6.5h.01M15.5 6.5h.01" /></svg>
);
// Солнце и луна — кнопка «темы» в шапке (AccountThemeButton ниже): значок показывает
// ТЕКУЩУЮ тему (солнце в светлой, луна в тёмной), тап её переключает.
const SunIcon = () => (
  <svg viewBox="0 0 24 24" width={20} height={20} {...iconProps}><circle cx="12" cy="12" r="4.1" /><path d="M12 2.9v2.3M12 18.8v2.3M4.7 4.7l1.6 1.6M17.7 17.7l1.6 1.6M2.9 12h2.3M18.8 12h2.3M4.7 19.3l1.6-1.6M17.7 6.3l1.6-1.6" /></svg>
);
// Луна — ЗАЛИТАЯ, как родная кнопка (moon.fill; кадр iOS K08-account-root-dark).
const MoonIcon = () => (
  <svg viewBox="0 0 24 24" width={20} height={20} {...iconProps} fill="currentColor"><path d="M20.2 14.6A8.6 8.6 0 0 1 9.4 3.8a8.6 8.6 0 1 0 10.8 10.8z" /></svg>
);
/** Значок «Настройки» — шестерёнка (владелец 2026-08-07: аватарка принадлежит
 *  «Аккаунту», у «Настроек» везде остаётся шестерёнка). */
export const GearIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M19.2 14.6a1.6 1.6 0 0 0 .32 1.76l.06.06a1.9 1.9 0 1 1-2.7 2.7l-.06-.06a1.6 1.6 0 0 0-1.76-.32 1.6 1.6 0 0 0-.97 1.46V20.4a1.9 1.9 0 1 1-3.8 0v-.09a1.6 1.6 0 0 0-1.03-1.46 1.6 1.6 0 0 0-1.76.32l-.06.06a1.9 1.9 0 1 1-2.7-2.7l.06-.06a1.6 1.6 0 0 0 .32-1.76 1.6 1.6 0 0 0-1.46-.97H3.6a1.9 1.9 0 1 1 0-3.8h.09a1.6 1.6 0 0 0 1.46-1.03 1.6 1.6 0 0 0-.32-1.76l-.06-.06a1.9 1.9 0 1 1 2.7-2.7l.06.06a1.6 1.6 0 0 0 1.76.32h.07a1.6 1.6 0 0 0 .97-1.46V3.6a1.9 1.9 0 1 1 3.8 0v.09a1.6 1.6 0 0 0 .97 1.46 1.6 1.6 0 0 0 1.76-.32l.06-.06a1.9 1.9 0 1 1 2.7 2.7l-.06.06a1.6 1.6 0 0 0-.32 1.76v.07a1.6 1.6 0 0 0 1.46.97h.09a1.9 1.9 0 1 1 0 3.8h-.09a1.6 1.6 0 0 0-1.46.97z" />
  </svg>
);

/** Значок «Аккаунт» — общий для пункта колонки и строки-карточки списка. */
export const AccountIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}><circle cx="12" cy="8.5" r="3.8" /><path d="M4.5 20c1.2-3.6 4-5.5 7.5-5.5s6.3 1.9 7.5 5.5" /></svg>
);
// ───────────── Тема и язык — ДВЕ СТРОКИ самого списка «Аккаунта» ─────────────
// Владелец 2026-08-10: страницы «Внешний вид» больше НЕТ. Оттенок и примерки облика
// выбирать нечего (схема одна и она по умолчанию), а ради двух настроек отдельную
// страницу не открывают — тема и язык стоят строками в списке и переключаются на месте.
// В колонке широкого экрана это те же два пункта: тап меняет значение, справа видно
// текущее. Единственные пункты меню без содержимого справа — содержимого у них и нет.

/** Тема кабинета. Истина — атрибут <html data-theme> (его же чинит страж theme.tsx),
 *  поэтому оба списка (колонка и страница) показывают одно и то же: наблюдаем атрибут,
 *  а не держим свою копию. */
export function useThemeAttr(): ['light' | 'dark', () => void] {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  useEffect(() => {
    const d = document.documentElement;
    const read = () => setTheme((d.getAttribute('data-theme') as 'light' | 'dark') ?? 'light');
    read();
    const mo = new MutationObserver(read);
    mo.observe(d, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, []);
  const flip = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    // Полоска статус-бара — сразу на переключателе, не дожидаясь наблюдателя темы (О9).
    applyTopStrip();
    try {
      localStorage.setItem('scriptera-theme', next);
    } catch {
      /* приватный режим */
    }
    setTheme(next);
  };
  return [theme, flip];
}

/** Язык кабинета: персист users.lang (база — истина языка вошедшего: гейт кабинета,
 *  письма, другие устройства) + живая подмена словаря НА МЕСТЕ (locale-live.tsx).
 *  Никакой навигации и перезагрузок (заказ владельца 2026-07-23). СНАЧАЛА дождаться
 *  записи users.lang, ПОТОМ менять словарь.
 *
 *  INVARIANT(scriptera): смена языка кабинета идёт ТОЛЬКО этой рукой — запрос
 *  /api/cabinet/lang, затем switchTo живого словаря. Причина: router.replace/reload и
 *  серверные getTranslations живую смену ломают (владелец 2026-07-23 отклонил три таких
 *  компромисса). Канон: CLAUDE.md «Смена языка кабинета — только живая, на месте».
 *  Пункт «Язык и регион» (Н5.2-в1) взял ту же руку — второй механики языка нет. */
export function useSetLang(): {
  /** ЯЗЫК ДЛЯ ГАЛОЧКИ: выбранный человеком, даже пока словарь ещё грузится. */
  lang: 'ru' | 'en';
  setLang: (lang: 'ru' | 'en') => void;
  /** Запись users.lang не прошла — язык вернулся обратно, на экране заметка. */
  failed: boolean;
} {
  const locale = useLocale() as 'ru' | 'en';
  const { switchTo } = useLiveLocale();
  // ОПТИМИСТИЧНЫЙ ВЫБОР (владелец 11.09.2026, П12 обхода: «галочки стоят у обоих»).
  // `switchTo` ждёт чанк словаря, и до его приезда useLocale отдаёт ПРЕЖНИЙ язык —
  // ровно на это время галочка не переезжала. Держим выбранный язык у себя и отдаём
  // его вместо locale, пока словарь не догнал.
  const [pending, setPending] = useState<'ru' | 'en' | ''>('');
  const [failed, setFailed] = useState(false);
  // Правка состояния ПРЯМО В РЕНДЕРЕ — так React и советует забывать прежнее значение:
  // словарь догнал выбор, память больше не нужна.
  if (pending && pending === locale) setPending('');
  const setLang = (lang: 'ru' | 'en'): void => {
    if (lang === (pending || locale)) return;
    const prev = locale;
    setPending(lang);
    setFailed(false);
    // СНАЧАЛА ПОДМЕНА СЛОВАРЯ, ПОТОМ СЕРВЕР (владелец 09.09.2026: «язык меняется с
    // задержкой, галочки стоят на обоих»). Второй словарь прогрет заранее (locale-live),
    // поэтому смена — мгновенная и не ждёт сети; запись в базу идёт следом в фоне.
    // Раньше было наоборот: сначала PATCH, и всё ждало круга к серверу.
    void switchTo(lang);
    void fetch('/api/cabinet/lang', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ lang }),
    })
      .then((r) => {
        if (r.ok) return;
        throw new Error('lang');
      })
      // ОТКАТ (владелец 11.09.2026, П12): база — истина языка вошедшего, и если запись не
      // прошла, кабинет на другом устройстве и письма останутся на прежнем языке. Значит
      // и здесь язык возвращаем, а человеку говорим словами, что не сохранилось.
      .catch(() => {
        setPending('');
        setFailed(true);
        void switchTo(prev);
      });
  };
  return { lang: pending || locale, setLang, failed };
}

/** УГЛОВЫЕ КНОПКИ ШАПКИ «Аккаунта» на узком виде (владелец 29.08.2026): СЛЕВА — тема,
 *  СПРАВА — колокольчик уведомлений. Прежняя двойная кнопка-капсула «тема + колокольчик»
 *  (12.08) разошлась по углам: место слева освободила пилюля токенов (вход в историю
 *  токенов спрятан — пополнение токенов позже, владелец 29.08).
 *  Обе — обычные кружки шапки (.acctbtn), одна серия с соседями.
 *  В приложении веб-шапку прячет CSS, но кнопки живы в DOM: нативные кнопки шапки жмут
 *  именно их (shell.tsx, ключи top:flip-theme и top:notify-inbox). */

/** Смена темы НА МЕСТЕ (атрибут <html data-theme>): значок показывает текущую тему. */
export function AccountThemeButton({ className }: { className?: string } = {}) {
  const t = useTranslations('cabinet.settings');
  const [theme, flipTheme] = useThemeAttr();
  return (
    <KBareButton
      className={className ? `acctbtn ${className}` : 'acctbtn'}
      aria-label={t('theme')}
      title={`${t('theme')} · ${t(theme === 'dark' ? 'themeDark' : 'themeLight')}`}
      onClick={flipTheme}
    >
      {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
    </KBareButton>
  );
}

/** Колокольчик с наклейкой непрочитанного: открывает ТОТ ЖЕ центр уведомлений, что
 *  угловая кнопка страницы «Уведомления» (useOpenInbox) — второй механики открытия нет.
 *  Цифра наклейки — СЕРВЕРНЫЙ счёт непрочитанного (useInboxBadge, не длина загруженной
 *  страницы ленты) и обновляется при возврате на страницу. */
export function AccountInboxButton() {
  const t = useTranslations('cabinet.settings');
  const openInbox = useOpenInbox();
  const unread = useInboxBadge();
  return (
    <KBareButton
      className="acctbtn acct-bell acct-top-bell"
      // Имя кнопки — то, что она открывает («Полученные», панель 460), а не «Уведомления»: так звалась
      // и строка колонки «Аккаунта», и тап по слову попадал в колокольчик вместо страницы в поле
      // (пакет Д2.2 п.14, кадр d1-1280/X-notifications).
      aria-label={t('notifInbox')}
      title={t('notifInbox')}
      onClick={() => openInbox()}
    >
      <BellIcon />
      {unread > 0 ? <span className="acct-bell-badge">{unread > 99 ? '99+' : unread}</span> : null}
    </KBareButton>
  );
}

// ─────────────────────────── Разделы «Безопасность» и «Уведомления» (только приложение) ───────────────────────────
// Владелец 2026-07-21: Push и Face ID — не строки-переключатели в общем списке, а
// собственные разделы, открывающие шторку. Внутри шторки — веб с одним пунктом
// (вид как «Внешний вид»); разделы будут прирастать пунктами позже.
// Значков у самих тумблеров «Безопасности» больше нет: строка-тумблер набора (KSwitch)
// — это имя жирным + переключатель справа + пояснение снизу, места под плитку значка в
// ней не предусмотрено (канон «Элементы формы»).
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    {/* Контур без галочки — как значок «Безопасности» в iOS (ТЗ WEB-PARITY-3 К12, кадр K06). */}
    <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
  </svg>
);
const BellIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}>
    <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
    <path d="M10.3 19a2 2 0 0 0 3.4 0" />
  </svg>
);

/** Содержимое шторки «Безопасность»: вход по Face ID (только в приложении, состояние
    держит натив) и подтверждение входа в Telegram (серверный флаг tfa_telegram).
    Код-пароль тут не заводим — его спрашивает сама iOS (решение владельца 2026-07-25). */
function SecuritySheet() {
  const t = useTranslations('cabinet.settings');
  const faceId = useFaceId();
  // ДВЕ ГРУППЫ ПО СМЫСЛУ (владелец 09.09.2026): «Каждое открытие» и «Вход с нового
  // устройства» — ровно те же, что у родной страницы приложения (экраны совпадают).
  // Прежний общий пузырь с именем securityMeta отменён этим решением.
  return (
    <FPane>
      {faceId.state ? (
        <FBlock label={t('secEveryOpen')} hint={t('faceIdNote')}>
          <KSwitch
            checked={faceId.state.enabled}
            label={t('faceId')}
            onChange={(v) => faceId.toggle(v)}
          />
        </FBlock>
      ) : null}
      <TfaTelegramRow />
    </FPane>
  );
}

/** ВХОД ПО FACE ID — САМА НАСТРОЙКА, БЕЗ РАЗМЕТКИ (docs/NATIVE-ACCOUNT-PLAN.md, этап Б):
 *  её показывают и веб-шторка «Безопасность» выше, и РОДНАЯ страница приложения. Состояние
 *  держит натив (веб только спрашивает и переключает), поэтому вне приложения `state`
 *  остаётся null — строки нет вовсе. */
function useFaceId(): { state: { enabled: boolean } | null; toggle: (on: boolean) => void } {
  const [state, setState] = useState<{ enabled: boolean } | null>(null);
  useEffect(() => {
    nativeBridge()
      ?.getState()
      .then((s) => {
        if (s.available) setState({ enabled: !!s.faceIdEnabled });
      })
      .catch(() => {});
  }, []);
  const toggle = (on: boolean) => {
    setState({ enabled: on });
    nativeBridge()
      ?.setFaceIdEnabled({ enabled: on })
      // Включение гоняет живую проверку Face ID: отказ → тумблер возвращаем.
      .catch(() => setState({ enabled: false }));
  };
  return { state, toggle };
}

/** Второй шаг входа: после кода из письма бот спрашивает «это вы?». Без привязанного
    (и не остановленного) Telegram включить нельзя — иначе человек запрёт себя сам.
    Пузырь со своим заголовком «Вход с нового устройства» — тот же, что у родной страницы
    приложения (владелец 09.09.2026); настройка ещё не доехала — пузыря нет вовсе, а не
    пустой пузырь с заголовком. */
function TfaTelegramRow() {
  const t = useTranslations('cabinet.settings');
  const { on, usable, meta, ready, toggle, dialogs } = useTfaTelegram();
  if (!ready) return null;
  // ТЕЛЕФОН В БРАУЗЕРЕ (сверка 03.10, кадр iOS K06-security): карточка-тумблер — строка списка
  // «заголовок · тумблер справа», подпись под карточкой; правила пузырей форм растягивали
  // KSwitch на всю ширину (серый круг вместо тумблера, заголовок по центру).
  if (readPhoneLook()) {
    return (
      // Подпись под карточкой — сноской раздела (13/17 третичным, 4 от кромки; кадр K06).
      <KListSection label={t('secNewDevice')} hint={t('tfaTgNote')}>
        <KListGroup>
          <KListRow
            title={t('tfaTg')}
            {...(meta ? { hint: meta } : {})}
            control
            value={<KSwitch checked={on} disabled={!usable} ariaLabel={t('tfaTg')} onChange={toggle} />}
          />
        </KListGroup>
        {dialogs}
      </KListSection>
    );
  }
  return (
    <FBlock label={t('secNewDevice')} hint={t('tfaTgNote')}>
      <KSwitch
        checked={on}
        disabled={!usable}
        label={t('tfaTg')}
        hint={meta}
        onChange={toggle}
      />
      {dialogs}
    </FBlock>
  );
}

/** САМА НАСТРОЙКА второго шага — без разметки (её берут и веб-строка выше, и РОДНАЯ
 *  страница «Безопасность» в приложении). Второй ручки и второго кэша не заводить. */
function useTfaTelegram(): {
  ready: boolean;
  on: boolean;
  usable: boolean;
  meta?: string;
  toggle: () => void;
  /** Окна выключения (FUNNEL-TZ §5.2 п.7): «войдите заново» и подтверждение в Telegram/кодом.
   *  Веб-строка рендерит их рядом с тумблером; в приложении их заменяют родные окна. */
  dialogs: ReactNode;
} {
  const t = useTranslations('cabinet.settings');
  const { logout } = useLogout();
  const [stepUp, setStepUp] = useState(false);
  const [confirm, setConfirm] = useState<TfaOffConfirm | null>(null);
  // Общий кэш кабинета (CLAUDE.md п.5): повторное открытие «Безопасности» показывает
  // прежнее состояние сразу, свежее доезжает фоном — без загрузки «с нуля» каждый раз.
  const { data, mutate } = usePersistentData<{ ok: boolean; tfaTelegram: boolean; tgLinked: boolean; tgBlocked: boolean }>(
    SECURITY_KEY,
    SECURITY_URL,
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const usable = !!data && data.tgLinked && !data.tgBlocked;
  // ВЫКЛЮЧЕНИЕ второго шага — только с подтверждением (FUNNEL-TZ §5.2 п.7, владелец 07.10):
  // ручка отвечает 403 confirm_required (вопрос ушёл в Telegram / код на почту) или
  // 403 step_up (сессия по ссылке из сообщения — войти заново). Тумблер возвращаем назад
  // и ведём человека через окно; флаг гаснет только по ok ручки.
  const confirmInApp = (st: TfaOffConfirm, prev: NonNullable<typeof data>) => {
    if (st.via === 'tg') {
      void pollTfaOff(SECURITY_URL, st.token).then((r) => {
        if (r === 'ok') mutate({ ...prev, tfaTelegram: false });
      });
      const text = st.tgName ? t('tfaOffTgBody', { acc: st.tgName }) : t('tfaOffTgBodyNoAcc');
      if (hasNativeNotice()) void showNativeNotice({ style: 'card', icon: 'paperplane', title: t('tfaOffTgTitle'), text, primary: t('tfaOffCancel') });
      else void askNativeConfirm({ question: t('tfaOffTgTitle'), message: text, confirmLabel: t('tfaOffCancel'), cancelLabel: t('tfaOffCancel'), destructive: false });
      return;
    }
    // Код на почту вводится в кабинете на сайте — родного поля ввода в этой сборке нет.
    if (hasNativeNotice()) void showNativeNotice({ style: 'card', title: t('tfaOffEmailTitle'), text: t('tfaOffEmailApp'), primary: t('tfaOffCancel') });
    else void askNativeConfirm({ question: t('tfaOffEmailTitle'), message: t('tfaOffEmailApp'), confirmLabel: t('tfaOffCancel'), cancelLabel: t('tfaOffCancel'), destructive: false });
  };
  const stepUpInApp = () => {
    void askNativeConfirm({ question: t('stepUpTitle'), message: t('stepUpBody'), confirmLabel: t('stepUpLogout'), cancelLabel: t('stepUpLater'), destructive: false }).then((yes) => {
      if (yes) void logout({ to: '/login' });
    });
  };
  const toggle = () => {
    if (!data || !usable) return;
    const next = !data.tfaTelegram;
    const prev = data;
    // ВЫКЛЮЧЕНИЕ — БЕЗ ОПТИМИЗМА (владелец 11.10.2026, TARIFF-PAGE-TZ §11 П9: тумблер гас до ответа сервера,
    // и защита выглядела снятой одним нажатием): «выключено» встаёт только по `ok` ручки — после
    // подтверждения в Telegram / кодом с почты. Включение подтверждения не требует — показываем сразу.
    if (next) mutate({ ...data, tfaTelegram: next });
    fetch(SECURITY_URL, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ tfaTelegram: next }),
    })
      .then(async (r) => {
        const d = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string; via?: 'tg' | 'email'; token?: string; tgName?: string | null };
        if (d.ok) {
          mutate({ ...prev, tfaTelegram: next });
          return;
        }
        mutate({ ...prev, tfaTelegram: !next });
        if (next) return;
        if (isStepUp(r, d)) {
          if (hasNativeConfirm()) stepUpInApp();
          else setStepUp(true);
          return;
        }
        if (r.status === 403 && d.error === 'confirm_required') {
          const st: TfaOffConfirm = d.via === 'tg' && d.token ? { via: 'tg', token: d.token, tgName: d.tgName ?? null } : { via: 'email' };
          if (hasNativeConfirm()) confirmInApp(st, prev);
          else setConfirm(st);
        }
      })
      .catch(() => mutate({ ...prev, tfaTelegram: !next }));
  };
  const dialogs = (
    <>
      <StepUpDialog open={stepUp} onOpenChange={setStepUp} />
      <TfaOffConfirmDialog
        state={confirm}
        url={SECURITY_URL}
        onDone={() => {
          setConfirm(null);
          if (data) mutate({ ...data, tfaTelegram: false });
        }}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
  return {
    ready: !!data,
    on: !!data?.tfaTelegram,
    usable,
    // Серых описаний у строк настроек нет (владелец 2026-07-25) — meta показываем только
    // когда включить нельзя и человеку надо объяснить, почему.
    meta: !data ? undefined : !data.tgLinked ? t('tfaTgNone') : data.tgBlocked ? t('tfaTgBlocked') : undefined,
    toggle,
    dialogs,
  };
}

/** Значок «Справки» — вопрос в кружке: раздел про то, как всё устроено. */
const HelpIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}><circle cx="12" cy="12" r="9" /><path d="M9.6 9.4a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.7-.9 1.3v.4" /><path d="M12 17h.01" /></svg>
);

/** ОТКРЫТЬ СПРАВКУ — ОДНА РУКА НА ВЕБ-СТРОКУ И НА РОДНУЮ (строка «Справка» в
 *  «Настройках»). В браузере на телефоне и в двух слоях — боковым слоем кабинета (help-layer.tsx,
 *  ТЗ ANIM-FIX О5 П2), в трёх слоях — правым полем «Аккаунта» по адресу /app/account/help
 *  (HelpPane, ТЗ ANIM-FIX О6); в приложении —
 *  родным листом-стопкой поверх кабинета, и с `?app=1`: по нему справка прячет статьи
 *  про подписку и заработок (запрет №7 — покупок в приложении нет).
 *
 *  ЛЕСТНИЦА ЗАПАСНЫХ ХОДОВ (владелец 09.09.2026, ТЗ этап G п.2): сначала НАШ лист
 *  (`openHelpSheet` — наше содержимое, наша шапка с крестиком, без адресной строки и
 *  кнопок Сафари); сборка постарше этого метода не имеет — прежний лист внутреннего
 *  браузера (`openWebSheet`); нет и его — новое окно. Контракт моста заморожен
 *  (CLAUDE.md запрет №8): `webSheetOpen` не тронут, у справки свой новый метод. */
function useOpenHelp(): () => void {
  const locale = useLocale();
  const t = useTranslations('cabinet.settings');
  const [themeAttr] = useThemeAttr();
  const openHelpLayer = useOpenHelpLayer();
  return () => {
    const app = isNativeApp();
    // ТЕЛЕФОН В БРАУЗЕРЕ — БОКОВЫМ СЛОЕМ КАБИНЕТА (ТЗ ANIM-FIX О5 П2, видео владельца 04.10.2026:
    // полная навигация на /help шла без хода и меняла адресную строку). Три слоя — правым полем
    // «Аккаунта» (О6), приложение — родными экранами.
    if (!app) {
      openHelpInBrowser(openHelpLayer, '');
      return;
    }
    // РОДНАЯ СПРАВКА (владелец 10.09.2026) — первой ступенью лестницы: раздел рисует
    // натив тремя экранами стопки, а данные ему шлёт водитель (useNativeHelp). Вернула
    // false — сборка так не умеет (или водителя нет на экране): дальше всё как было.
    if (openNativeHelp()) return;
    const url = `${window.location.origin}/${locale}/help${app ? '?app=1' : ''}`;
    if (openHelpSheet(url, themeAttr, t('helpRow'))) return;
    if (openWebSheet(url, themeAttr)) return;
    if (app) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    window.location.href = url;
  };
}

/** ОТКРЫТЬ ПОЛИТИКУ КОНФИДЕНЦИАЛЬНОСТИ / СОГЛАШЕНИЕ — та же лестница, что у справки
 *  (владелец 29.09.2026, первая отправка в App Store: Apple требует ссылку на политику
 *  ВНУТРИ приложения — guideline 5.1.1). В приложении страница открывается нашим листом
 *  с `?bare=1` — юр-страница без шапки сайта и подвала (ссылка на главную с тарифами
 *  внутри приложения недопустима, CLAUDE.md п.7). В браузере — та же вкладка. */
function useOpenLegal(): (kind: 'privacy' | 'terms') => void {
  const locale = useLocale();
  const t = useTranslations('cabinet.settings');
  const [themeAttr] = useThemeAttr();
  const openHelpLayer = useOpenHelpLayer();
  return (kind) => {
    const app = isNativeApp();
    // В БРАУЗЕРЕ документ — уровень справки кабинета (ТЗ ANIM-FIX О6, решение владельца
    // 04.10.2026): слоем на телефоне и в двух слоях, правым полем в трёх.
    if (!app) {
      openHelpInBrowser(openHelpLayer, kind);
      return;
    }
    const path = kind === 'privacy' ? 'privacy' : 'oferta';
    const url = `${window.location.origin}/${locale}/${path}?bare=1`;
    const title = t(kind === 'privacy' ? 'privacyRow' : 'termsRow');
    if (app && openHelpSheet(url, themeAttr, title)) return;
    if (app && openWebSheet(url, themeAttr)) return;
    if (app) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    window.location.href = url;
  };
}

/** Справка (или её уровень `at`: 'privacy' | 'terms') в браузере: в трёх слоях — правым полем
 *  «Аккаунта» по адресу (тихий pushState, как вкладки кабинета; из-за пределов раздела —
 *  настоящая навигация), иначе — боковым слоем (help-layer.tsx). */
function openHelpInBrowser(openHelpLayer: (at?: string) => void, at: string): void {
  if (readLayoutMode() !== 'three') {
    openHelpLayer(at || undefined);
    return;
  }
  const href = `${pickToPath('account', 'help')}${at ? `/${at}` : ''}`;
  if (/\/app\/account(?:\/|$)/.test(window.location.pathname)) window.history.pushState(null, '', href);
  else window.location.assign(href);
}

/** Значок «Диагностики» — линия пульса: раздел про то, как приложение себя чувствует. */
const PulseIcon = () => (
  <svg viewBox="0 0 24 24" {...iconProps}><path d="M3 12h4l2.5-6 4 12L16 12h5" /></svg>
);

/**
 * Страница «Диагностика» (владелец 25.08.2026: «я должен иметь возможность скинуть тебе
 * журнал, а ты его проанализируешь»). Кнопка собирает журнал страницы (client-journal:
 * тапы по вкладкам, фактические переходы, наши запросы с кодами и длительностями, уходы
 * в фон, причины перезагрузок, вызовы нативного моста) и, если сборка приложения умеет
 * diagLog, прикладывает родной лог оболочки. Уезжает в client_journals — смотрим в
 * админке, «Ошибки» → «Журналы клиентов».
 *
 * Цен, оплат и валют тут нет и быть не может (правило 7 позиционирования).
 */
type DiagState = 'idle' | 'busy' | 'done' | 'fail';

/** РУКА ДИАГНОСТИКИ — ОДНА НА ВЕБ-СТРАНИЦУ И НА РОДНУЮ (этап Д): собирает журнал
 *  страницы, прикладывает родной лог оболочки (если сборка умеет diagLog) и уезжает в
 *  client_journals. Второй отправки журнала в кабинете нет. */
function useSendDiagnostics(): { state: DiagState; send: () => void } {
  const [state, setState] = useState<DiagState>('idle');
  const send = () => {
    if (state === 'busy') return;
    setState('busy');
    void (async () => {
      // Родной лог оболочки прикладывает сам sendJournal (канон DIAGNOSTICS-CANON: к каждому
      // журналу, не только к ручному).
      const id = await sendJournal('manual', { full: true });
      setState(id ? 'done' : 'fail');
    })();
  };
  return { state, send };
}

function DiagnosticsPanel() {
  const t = useTranslations('cabinet.settings');
  const { state, send } = useSendDiagnostics();
  return (
    <FPane>
      <FBlock label={t('diagBlock')}>
        <FHint>{t('diagBody')}</FHint>
        <KButton tone="primary" loading={state === 'busy'} onClick={send}>
          {t('diagSend')}
        </KButton>
        {state === 'done' ? <FHint>{t('diagDone')}</FHint> : null}
        {state === 'fail' ? <FError>{t('diagFail')}{lastJournalError ? ` (${lastJournalError})` : ''}</FError> : null}
      </FBlock>
    </FPane>
  );
}

/** Страница «Язык и регион» (владелец 21.08, Н5.2-в1): язык кабинета, страна, валюта и
 *  часовой пояс — в этом порядке (страна и валюта переехали из «Профиля», MULTICURRENCY-PLAN
 *  О5, владелец 07.10.2026; поля — geo-field.tsx, в приложении строки «Валюта» нет).
 *
 *  ЯЗЫК — ПЕРЕЕЗД, А НЕ НОВАЯ МЕХАНИКА: та же рука useSetLang (запись users.lang + живая
 *  подмена словаря locale-live). Никаких навигаций, перезагрузок и серверных
 *  getTranslations в /app — канон «Смена языка кабинета — только живая, на месте».
 *  ЧАСОВОЙ ПОЯС — общий компонент TimezoneField: вторая его дверь живёт в «Тихих часах»,
 *  своей разметки пояса на странице нет. */
function LocaleRegionPanel() {
  const t = useTranslations('cabinet.settings');
  const { lang, setLang, failed } = useSetLang();
  return (
    <FPane>
      <FChoiceBlock
        label={t('language')}
        value={lang}
        options={[
          // Языки подписаны САМИ СОБОЙ (владелец: «Русский» и «English» одинаково в обеих
          // локалях) — переводить названия языков нечего.
          { value: 'ru', label: 'Русский' },
          { value: 'en', label: 'English' },
        ]}
        onChange={(v) => setLang(v === 'en' ? 'en' : 'ru')}
      />
      {/* Язык не записался — язык вернулся, и человек об этом знает (П12, 11.09.2026). */}
      {failed ? <FError>{t('languageError')}</FError> : null}
      {/* Страна и валюта — между языком и поясом (О5); подпись «Регион» на телефоне — над ними. */}
      <GeoFields />
      {/* Пояс — следом, на телефоне без своей подписи: «Регион» уже стоит над страной. */}
      <TimezoneField phoneCaption={null} />
    </FPane>
  );
}

// ─────────────────────────── Своды разделов «Настроек» и «Аккаунта» ───────────────────
// КОНЦЕПЦИЯ ОТКРЫТИЯ (владелец 2026-08-05, ЖЕЛЕЗНОЕ). Есть вторая колонка (широкий
// экран) — пункт меню открывает содержимое СПРАВА, в поле контента; шторок и модалок
// из этого меню НЕ бывает ни у одного пункта. Нет второй колонки (узкий веб, приложение)
// — сам список пунктов и есть содержимое, поэтому каждый пункт открывается ШТОРКОЙ.
// Отсюда: у КАЖДОГО раздела свода обязан быть `body` (иначе справа откроется пустота).
//
// ДВА РАЗДЕЛА ГЛАВНОГО МЕНЮ (владелец 2026-08-07, отменяет один общий список):
//   • «Настройки» (/app/settings) — рабочие справочники и доступы: Подключения,
//     Контакты, Продукты, Данные людей, MCP-сервер, Сотрудники (· Кабинеты);
//   • «Аккаунт» (/app/account) — личное: Профиль, Подписка, Токены, Партнёрам
//     (· Заработок), Внешний вид, Уведомления, Безопасность.
// Ключ раздела = КУСОК АДРЕСА (/app/settings/mcp, /app/account/subscription): своих
// табличек «ключ ↔ путь» не заводим, переезд старых адресов — SETTINGS_MOVED_TO_ACCOUNT
// в lib/cabinet-urls.ts.
//
// ОДИН ВИД У ВСЕХ СТРАНИЦ обоих разделов (владелец 2026-08-07): заголовок и кнопки
// страницы даёт <PaneHead> (эталон — «Все боты»), тело — примитивы drawer-kit и пузыри
// .ai-vgroup. Своих «хиро по центру» и одноразовой вёрстки у страниц нет.

/** Строка хвостового пузыря свода (см. SectionRows.after). */
type AfterRow = { title: string; color: string; icon: React.ReactNode; onClick: () => void };

/**
 * ПЕРЕКЛЮЧАТЕЛЬ СЕРВЕРА в iOS-приложении (владелец 01.10.2026): строка «Сервер» в «Аккаунте»
 * видна только своим учёткам (lib/our-accounts.ts) и только в сборке с методом моста
 * `serverSet`. Тап переводит ЭТО приложение на другой сервер — прод или staging
 * (`dev.scriptera.ai`, docs/HOW-IT-WORKS.md §2): натив запоминает адрес и перезапускает веб
 * на нём. На серверы, клиентов и чужие телефоны это не влияет.
 */
export function onStagingNow(): boolean {
  return typeof window !== 'undefined' && window.location.origin === STAGING_ORIGIN;
}
export function toggleNativeServer(): void {
  const br = nativeBridge();
  if (!br?.serverSet) return;
  void br.serverSet({ origin: onStagingNow() ? PROD_ORIGIN : STAGING_ORIGIN }).catch(() => {});
}

export interface SettingsSection {
  key: string;
  title: string;
  color: string;
  icon: React.ReactNode;
  /** Блок меню: между блоками в колонке — черта, на узком виде отдельный пузырь.
   *  В своде «Настроек» их четыре: 1 — «Данные», 2 — «Папки» (владелец 13.09.2026, П85),
   *  3 — остальное, 4 — «Диагностика». В своде «Аккаунта» — первые два. */
  group: 1 | 2 | 3 | 4 | 5;
  /** Короткая подпись героя шторки на узком виде (в колонке и заголовке не участвует). */
  meta?: string;
  /** Тело страницы — то же, что открывает строка, но БЕЗ героя: белая шапка-градиент
   *  живёт только на тёмном слое шторки, на светлой странице её текста не видно. */
  body: React.ReactNode;
  /** Страница рисует свой герой сама (имя тарифа у «Подписки») — общий не рисуем. */
  noHero?: boolean;
  /** Кнопка страницы в шапке шторки: SF-иконка для нативного верха + DOM-фолбэк. */
  action?: { icon: string; label: string; onClick: () => void; dom: React.ReactNode };
  /** Пункт-ПЕРЕКЛЮЧАТЕЛЬ (тема и язык, владелец 2026-08-10): страницы у него нет —
   *  тап меняет значение на месте. `label` — текущее значение словом (серым в строке
   *  списка и в колонке), `onToggle` — само переключение. */
  flip?: { label: string; onToggle: () => void };
  /** Пункт-ОКНО (PAY-SCREEN-TZ §1.4, ревизия клиента п.5): страницы у него нет — тап открывает
   *  форму стопкой (на телефоне лист, на широком панель 460), как «Изменить» на слое «Оплата».
   *  Канон: форма, что на телефоне открывается стопкой, на десктопе — панель, не поле. */
  open?: () => void;
  /** Наклейка «!» у пункта (FUNNEL-TZ §4.4): просрочка оплаты у «Подписки», только веб. */
  alert?: boolean;
}

/** Свод страниц раздела «Аккаунт» (владелец 2026-08-07): Профиль · Подписка · Токены ·
 *  Партнёрам (· Заработок) ‖ Внешний вид · Уведомления · Безопасность. «Профиль» —
 *  первый пункт и КОРЕНЬ адреса раздела (/app/account), его тело — редактор из
 *  profile.tsx. Без `user` (профиль ещё не пришёл) пункта профиля просто нет. */
export function useAccountSections(user?: ProfileInfo): SettingsSection[] {
  const t = useTranslations('cabinet.settings');
  const tb = useTranslations('cabinet.billing');
  const tov = useTranslations('cabinet.overview');
  const native = useNativeApp();
  const openHistory = usePaymentsDrawer();
  const openInbox = useOpenInbox();
  // Значок родной шапки — с точкой, когда есть непрочитанное (то же, что у кружка).
  const inboxUnread = useInboxBadge();
  // Просрочка оплаты — кружок «!» у «Подписки» (FUNNEL-TZ §4.4; в приложении пункта нет вовсе).
  const overdue = useOverdueBadge();
  const [theme, flipTheme] = useThemeAttr();
  // Переключатель сервера: есть ли метод моста и на каком сервере открыт веб сейчас
  // (после монтирования — на сервере адреса окна нет).
  const hasServerSet = useNativeMethod('serverSet');
  const [onStaging, setOnStaging] = useState(false);
  useEffect(() => setOnStaging(onStagingNow()), []);
  // «Заработок» — только включённым юзерам (тот же общий кэш, что у строки).
  // SPEED §5.3 (приёмка ведущего): флаг «Заработка» — когда раздел «Аккаунт» на экране, не на входе в кабинет.
  const accountVisible = usePaneVisible();
  const { data: earn } = usePersistentData<{ enabled?: boolean }>(
    'cabinet:earnings',
    accountVisible ? '/api/cabinet/earnings' : '',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );

  const out: SettingsSection[] = [];
  // «Профиль» — ПЕРВЫЙ пункт списка и КОРЕНЬ раздела (владелец 2026-08-10, отменяет
  // «профиля в списке нет» от 2026-08-07): нажатие на «Аккаунт» открывает именно его,
  // поэтому его ключ в адрес не пишется (/app/account, а не /app/account/profile —
  // пара живёт в shell.tsx, setAccPick). Тело — редактор из profile.tsx; без `user`
  // (колонка его не знает) остаётся один пункт меню, тело даёт сама страница.
  out.push({
    key: 'profile',
    title: t('profileRow'),
    color: 'var(--violet)',
    icon: <AccountIcon />,
    group: 1,
    body: user ? <ProfilePanel user={user} /> : null,
  });
  // ── БЛОК 1: деньги. В приложении его нет целиком (владелец 31.08.2026): подписка,
  // партнёрка и заработок — наши цены и выплаты, а «Токены» — вход в историю трат,
  // и в сторах мы не показываем ничего из этого (CLAUDE.md п.7).
  if (!native) {
    out.push({
      key: 'subscription',
      // Подпись КОРОТКАЯ (владелец 2026-08-02): в колонке это пункт меню, а не заголовок.
      title: t('menuBilling'),
      color: 'var(--amber)',
      icon: <BillingIcon />,
      group: 1,
      meta: t('billingMeta'),
      alert: overdue,
      body: <BillingPanel inline />,
      // Герой этой страницы рисует САМА панель (под иконкой — имя тарифа клиента),
      // поэтому общий герой свода тут не нужен (владелец 2026-08-09).
      noHero: true,
      // Круглая кнопка истории платежей — справа в шапке (эталон «Сотрудники»).
      action: {
        icon: 'clock.arrow.circlepath',
        label: tb('historyToggle'),
        onClick: openHistory,
        dom: <PaymentsHistoryButton />,
      },
    });
  }
  // «ТОКЕНЫ» — ТОЛЬКО В ВЕБЕ (владелец 31.08.2026): раздел открыт обратно обычным
  // пунктом свода, но в приложении его НЕ показываем вовсе (не прячем цифры внутри) —
  // запрет №7, платного в сторах нет. Кнопка «Отправить отчёт» — общим слотом action:
  // на широком в шапке правого поля, на узком в шапке шторки.
  if (!native) {
    out.push({
      key: 'tokens',
      title: tov('reportTitle'),
      color: 'var(--amber)',
      icon: <CoinsIcon />,
      group: 1,
      body: <SpendReportSheet />,
      // Герой «Баланс N» рисует сама страница (SettingsHero в SpendReportSheet, М57) — общий не рисуем.
      noHero: true,
      action: {
        icon: 'square.and.arrow.up',
        label: tov('shareTitle'),
        onClick: openSpendShareMenu,
        dom: <SpendShareButton />,
      },
    });
  }
  // «ТАРИФ» — СТРАНИЦА ПРИЛОЖЕНИЯ вместо «Подписки» (владелец 31.08.2026): в сторах
  // платного не показываем (запрет №7), но человек вправе видеть, что у него за тариф.
  // Только факты, без цен и без единого намёка, где платить, — см. PlanPanel.
  if (native) {
    out.push({
      key: 'plan',
      title: tb('planLabel'),
      color: 'var(--amber)',
      icon: <BillingIcon />,
      group: 1,
      body: <PlanPanel />,
    });
  }
  if (!native) {
    out.push({
      key: 'partner',
      title: t('menuPartner'),
      meta: t('partnerMeta'),
      color: 'var(--green)',
      // «Двое людей» вместо подарка (владелец 10.10, PAY-FLOW-TZ §16 К13 п.4).
      icon: <PeopleIcon />,
      group: 1,
      body: <PartnerPanel />,
    });
    if (earn?.enabled) {
      out.push({
        key: 'earnings',
        title: t('earningsRow'),
        color: 'var(--amber)',
        icon: <CoinsIcon />,
        group: 1,
        body: <EarningsPanel />,
      });
    }
  }
  // ── БЛОК 2: личные настройки самого кабинета.
  out.push({
    key: 'notifications',
    title: t('notificationsRow'),
    meta: t('notificationsMeta'),
    color: 'var(--amber)',
    icon: <BellIcon />,
    group: 2,
    body: <NotificationsSheet />,
    // «Полученные» — КРУГЛОЙ КНОПКОЙ В УГЛУ страницы (владелец 29.08.2026): строки в
    // теле больше нет. Механика та же, что у истории платежей «Подписки»: SF-значок
    // уходит в родную шапку приложения, DOM-кнопка стоит в шапке шторки и в шапке
    // страницы на широком экране. Открывает ту же руку (useOpenInbox).
    action: {
      icon: inboxUnread > 0 ? 'bell.badge' : 'bell',
      label: t('notifInbox'),
      onClick: () => openInbox(),
      dom: <InboxTopButton />,
    },
  });
  out.push({
    key: 'security',
    title: t('securityRow'),
    meta: t('securityMeta'),
    color: 'var(--teal)',
    icon: <ShieldIcon />,
    group: 2,
    body: <SecuritySheet />,
  });
  // «Язык и регион» (владелец 21.08, Н5.2-в1) — рядом с «Уведомлениями» и
  // «Безопасностью»: язык кабинета (переехал сюда из строки-переключателя и из шапки)
  // и часовой пояс. Обычный пункт свода — значит открывается ТЕМ ЖЕ способом, что
  // соседи: справа в колонке на широком, шторкой на узком (канон UI-TEMPLATE).
  out.push({
    key: 'locale',
    title: t('localeRow'),
    meta: t('localeMeta'),
    color: 'var(--blue)',
    icon: <GlobeIcon />,
    group: 2,
    body: <LocaleRegionPanel />,
  });
  // Тема — последней строкой блока (владелец 2026-08-10): страницы у неё нет, тап
  // переключает на месте. Языка тут больше нет (21.08): он внутри «Языка и региона».
  out.push({
    key: 'theme',
    title: t('theme'),
    color: 'var(--violet)',
    icon: <PaletteIcon />,
    group: 2,
    body: null,
    flip: {
      label: t(theme === 'dark' ? 'themeDark' : 'themeLight'),
      onToggle: flipTheme,
    },
  });
  // «Сервер: Прод / Dev» — переключатель сервера (см. toggleNativeServer): только свои
  // учётки и только сборка приложения с методом моста. Страницы у строки нет — тап
  // переключает; значение стоит в самом названии (у родной строки свода поля значения нет).
  if (user?.serverSwitch && hasServerSet) {
    out.push({
      key: 'server',
      title: t('serverRow', { name: t(onStaging ? 'serverDev' : 'serverProd') }),
      color: 'var(--blue)',
      icon: <GlobeIcon />,
      group: 2,
      body: null,
    });
  }
  // «Справка» — В КОРНЕ «АККАУНТА» (владелец 15.09.2026: перенесена из «Настроек»),
  // ОТДЕЛЬНЫМ пузырём ПОД «Настройками» (группа 4; «Диагностика» ушла в 5). На узком
  // вебе «Настройки» стоят пузырём `after` под сводом — там «Справка» идёт вторым таким
  // же пузырём (см. корень раздела). Открытый центр помощи (/help): страницы у неё в
  // кабинете нет — строку разбирают открывалки корня (веб — openSection, приложение —
  // onRow водителя), тело положено запаской, на экран оно не попадает.
  out.push({
    key: 'help',
    title: t('helpRow'),
    meta: t('helpMeta'),
    color: 'var(--blue)',
    icon: <HelpIcon />,
    group: 4,
    // Тела у строки нет: справку рисуют её открывалки (useOpenHelp), в трёх слоях — HelpPane в
    // правом поле (AccountPanel). Прежняя запаска-пилюля «Справка» (HelpRowBody) и была пустой
    // панелью из претензии владельца 04.10.2026 (ТЗ ANIM-FIX О6).
    body: null,
  });
  // «Политика конфиденциальности» и «Пользовательское соглашение» в «Аккаунте» больше
  // не стоят (владелец 29.09.2026: крупно и заметно) — они последней карточкой родной
  // «Справки» (useNativeHelp, onLegal). Apple требует политику внутри приложения —
  // справка открывается внутри него.
  return out;
}

/** Свод страниц раздела «Настройки» (владелец 2026-08-07): справочники и доступы. */
/** Справочники кабинета (владелец 2026-08-07): Подключения · Контакты · Продукты ·
 *  Данные людей. Свой свод — они идут ПЕРВЫМ блоком «Настроек» (отдельного экрана
 *  «Данные» больше нет: список из четырёх ссылок стоит сразу). */
export // Плитки — РАЗНЫХ цветов (владелец 29.08.2026: «четыре иконки одного цвета — сделать разных»).
function useDataSections(): SettingsSection[] {
  const t = useTranslations('cabinet.settings');
  const tsec = useTranslations('cabinet.sections');
  const rows: SettingsSection[] = [
    {
      key: 'connections',
      title: tsec('connections'),
      color: 'var(--green)',
      icon: <IconConnections />,
      group: 1,
      body: <DataSectionInline section="connections" />,
    },
    {
      key: 'contacts',
      title: tsec('contacts'),
      color: 'var(--blue)',
      icon: <IconContacts />,
      group: 1,
      body: <DataSectionInline section="contacts" />,
    },
    // «МЕТКИ» (П190, владелец 27.09.2026) — третьей после «Контактов»: все метки кабинета
    // с числом контактов; добавить, переименовать, удалить с подтверждением.
    {
      key: TAGS_ROW,
      title: t('tags.title'),
      color: TAGS_COLOR,
      icon: <TagIcon />,
      group: 1,
      body: <TagsPanel />,
    },
    {
      key: 'products',
      title: tsec('products'),
      color: 'var(--amber)',
      icon: <IconProducts />,
      group: 1,
      body: <DataSectionInline section="products" />,
    },
    {
      key: 'people-data',
      title: t('peopleDataRow'),
      meta: t('peopleDataMeta'),
      color: 'var(--violet)',
      icon: <PeopleDataIcon />,
      group: 1,
      body: <PeopleDataPanel />,
    },
  ];
  // «Продукты» и «Данные людей» спрятаны (владелец 31.08.2026, «пока нет интеграции с
  // платёжными системами») — lib/hidden-features.ts; код и адреса живы.
  return SETTINGS_PRODUCTS_PEOPLE_HIDDEN
    ? rows.filter((r) => r.key !== 'products' && r.key !== 'people-data')
    : rows;
}

/** Ключи строк пузыря «Папки» — по ним обе открывалки (веб и родная) узнают свою. */
export const FOLDERS_ROW = {
  bots: 'folders-bots',
  messages: 'folders-messages',
  contacts: 'folders-contacts',
} as const;

/**
 * ПУЗЫРЬ «ПАПКИ» (владелец 13.09.2026, П85): «Боты», «Чаты», «Контакты» — вторым блоком
 * свода, сразу после «Данных». До этого настройки папок открывались ТОЛЬКО шестерёнкой в
 * полосе папок самого раздела, и человек их не находил.
 *
 * КТО ОТКРЫВАЕТ. «Боты» — своя страница прямо тут (категории ботов живут в личном списке
 * юзера, идти за ними некуда). «Чаты» и «Контакты» — САМИ РАЗДЕЛЫ: у них своя стопка
 * страниц папок, свои данные и свои аккаунты, и второго хозяина этих настроек в кабинете
 * нет — «Настройки» только просят (lib/open-folders.ts).
 */
export function useFoldersSections(): SettingsSection[] {
  const tsec = useTranslations('cabinet.sections');
  return [
    {
      key: FOLDERS_ROW.bots,
      title: tsec('bots'),
      color: 'var(--accent-stroke)',
      icon: <FolderIcon />,
      group: 2,
      // Герой страницы — «Папки», а не имя строки «Боты» (кадр iOS B13-bots-folders-settings):
      // его рисует сама страница (BotsFoldersPanel), только у списка — у страницы папки героя нет.
      noHero: true,
      body: <BotsFoldersPanel />,
    },
    {
      key: FOLDERS_ROW.messages,
      title: tsec('messages'),
      color: 'var(--blue)',
      icon: <FolderIcon />,
      group: 2,
      // На телефоне строку разбирают обе открывалки (лист папок раздела), до тела дело не
      // доходит. На широком экране тело — СТРАНИЦА ПАПОК в поле (пакет Д1.2, владелец 04.10.2026:
      // «Папки → Чаты» в колонке открывает список папок с «Добавить папку», папка — панелью 460).
      body: <ChatFoldersInline />,
    },
    {
      key: FOLDERS_ROW.contacts,
      title: tsec('contacts'),
      color: 'var(--green)',
      icon: <FolderIcon />,
      group: 2,
      body: <ContactFoldersPage />,
    },
  ];
}

/** «Папки → Контакты» страницей в поле (Д1.2): аккаунты для условий папки — той же лёгкой
 *  ручкой, что у открывалки из «Настроек» (useOpenFolderSettings). */
function ContactFoldersPage() {
  const { data } = usePersistentData<{ ok: boolean; accounts: DialogAccount[] }>(
    'msg-accounts',
    '/api/cabinet/messages/accounts',
  );
  const accounts = useMemo(
    () =>
      (data?.accounts ?? []).map((a) => ({ value: a.key, label: a.name, hint: a.channel === 'tg' ? 'Telegram' : 'Instagram' })),
    [data],
  );
  return <ContactFoldersInline accounts={accounts} />;
}

/** Свод страниц «Настроек» БЕЗ справочников (владелец 2026-08-07): MCP-сервер ·
 *  Сотрудники (· Кабинеты). Справочники входят сюда одним пунктом «Данные». */
/** Значок «Платёжных данных» — карта (линейный, цвет — текста). */
const PayerCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="M3 10h18M7 15h4" />
  </svg>
);

export function useSettingsSections(acting = false): SettingsSection[] {
  const t = useTranslations('cabinet.settings');
  const native = useNativeApp();
  const openPayer = useOpenPayer();
  const ts = useTranslations('cabinet.staff');
  const { data: cabData } = useCabinets();
  const cabinetsRow: SettingsSection = {
    key: 'cabinets',
    title: ts('cabinetsRow'),
    meta: ts('cabinetsMetaSelf'),
    color: 'var(--blue)',
    icon: <CabinetsIcon />,
    group: 3,
    body: <CabinetSwitchPanel />,
  };
  // Чужой кабинет (STAFF-ROLE-PLAN Ф2.3): только переключалка кабинетов.
  if (acting) return [cabinetsRow];

  // «MCP-сервер» и «Сотрудники» спрятаны (владелец 29.08.2026, «пока что») —
  // lib/hidden-features.ts; остаётся только переключалка кабинетов, если их больше одного.
  const hiddenRows: SettingsSection[] = [
    {
      key: 'mcp',
      title: t('mcpRow'),
      meta: t('mcpMeta'),
      color: 'var(--teal)',
      icon: <McpIcon />,
      group: 3,
      body: <McpAppsPanel inline />,
    },
    {
      key: 'employees',
      title: ts('employeesRow'),
      meta: ts('employeesMeta'),
      color: 'var(--violet)',
      icon: <EmployeesIcon />,
      group: 3,
      body: <EmployeesInline />,
    },
  ];
  const out: SettingsSection[] = SETTINGS_MCP_STAFF_HIDDEN ? [] : hiddenRows;
  // «ПЛАТЁЖНЫЕ ДАННЫЕ» (~/projects/PAY-SCREEN-TZ.md §1.4): плательщик для касс и квитанций — та
  // же форма, что открывает «Изменить» у сводки на слое «Оплата». В iOS-приложении строки нет
  // (запрет №7: ни цен, ни оплат), родной список её не получает.
  if (!native) {
    out.push({
      key: 'billing-profile',
      title: t('billingProfileRow'),
      color: 'var(--green)',
      icon: <PayerCardIcon />,
      group: 3,
      body: null,
      open: () => openPayer(null, t('billingProfileRow')),
    });
  }
  if ((cabData?.cabinets ?? []).length > 1) out.push(cabinetsRow);
  // «Справка» с 15.09.2026 живёт в корне «Аккаунта» (useAccountSections), в «Настройках»
  // её больше нет.
  return out;
}

/** Все страницы «Настроек» одним списком — для поиска выбранной по адресу
 *  (/app/settings/<страница>): справочники и остальное лежат в разных сводах. */
export function useAllSettingsSections(acting = false): SettingsSection[] {
  const data = useDataSections();
  const folders = useFoldersSections();
  const rest = useSettingsSections(acting);
  const diag = useDiagSection();
  return acting ? rest : [...data, ...folders, ...rest, diag];
}

/** «Диагностика» — НЕ пункт списка (владелец 26.08.2026: «убери из списка, в настройки в
 *  самый низ, светлым мелким текстом»): страница есть (адрес /app/settings/diagnostics и
 *  шторка на узком), но в свод строк не входит — её открывает строка-текст под списком
 *  «Настроек» (SettingsPanel). */
function useDiagSection(): SettingsSection {
  const t = useTranslations('cabinet.settings');
  return {
    key: 'diagnostics',
    title: t('diagRow'),
    meta: t('diagMeta'),
    color: 'var(--teal)',
    icon: <PulseIcon />,
    group: 5,
    body: <DiagnosticsPanel />,
  };
}

/** Список пунктов раздела для УЗКОГО вида — СТРОКАМИ НАБОРА (владелец 12.08.2026: вид
 *  страницы «Уведомления» переносится на «Аккаунт»): корень FPane, на КАЖДЫЙ блок свода
 *  (поле `group`) свой пузырь FNotifyList, `after` — тоже отдельным пузырём (владелец
 *  2026-08-07: «Настройки» отдельно, а не последней строкой общего списка). Плитку значка
 *  34/11, шеврон и сводку рисует сам набор. Каждый пункт открывается ШТОРКОЙ — канон
 *  «нет колонки, всё шторкой». */
function SectionRows({
  sections,
  open,
  before,
  after,
  labels,
}: {
  sections: SettingsSection[];
  open: (s: SettingsSection) => void;
  /** Подписи над пузырями-блоками («Данные», «Папки» — кадр iOS K09-settings, ТЗ WEB-PARITY-3 К16). */
  labels?: Partial<Record<number, string>>;
  /** Блок НАД списком (герой профиля на «Аккаунте»). */
  before?: React.ReactNode;
  /** Строка ОТДЕЛЬНЫМ пузырём под списком («Настройки» на «Аккаунте»). */
  /** Хвостовые пузыри под сводом: элемент — одна строка (свой пузырь) или МАССИВ строк
   *  (один пузырь на несколько строк — «Политика» и «Соглашение» вместе). */
  after?: (AfterRow | AfterRow[])[];
}) {
  const groups = [1, 2, 3, 4, 5].filter((g) => sections.some((s) => s.group === g));
  return (
    <DGroup>
      {before}
      {/* set-rows: без верхнего отступа панели (24) — список стоит встык, как пузырь
          «Подключений» (владелец 29.08.2026: «отступы у пузыря как на других страницах»). */}
      <FPane className="set-rows">
        {groups.map((g) => (
          <FNotifyList
            key={g}
            {...(labels?.[g] ? { label: labels[g] } : {})}
            items={sections
              .filter((s) => s.group === g)
              .map((s) => ({
                key: s.key,
                label: s.title,
                icon: s.icon,
                iconColor: s.color,
                // Пункт-переключатель (тема, язык) — не ссылка на страницу: значение
                // серым под именем, шеврона нет, тап переключает на месте (тот же
                // обработчик, что у строки в колонке широкого вида — split-menu.tsx).
                ...(s.flip ? { summary: s.flip.label, chevron: false } : {}),
                // Наклейка «!» (просрочка у «Подписки», FUNNEL-TZ §4.4) — справа, перед шевроном.
                ...(s.alert ? { value: <OverdueDot /> } : {}),
                onOpen: s.flip ? s.flip.onToggle : (s.open ?? (() => open(s))),
              }))}
          />
        ))}
        {(after ?? []).map((a, i) => (
          <FNotifyList
            key={`after${i}`}
            items={(Array.isArray(a) ? a : [a]).map((r, j) => ({
              key: `after${i}-${j}`,
              label: r.title,
              icon: r.icon,
              iconColor: r.color,
              onOpen: r.onClick,
            }))}
          />
        ))}
      </FPane>
    </DGroup>
  );
}

/** ОКНО РАСШИРИЛИ, А СТРАНИЦА ОТКРЫТА ШТОРКОЙ (владелец 11.08.2026, та же беда, что в
 *  «Сообщениях»): на узком виде страница раздела живёт слоем поверх списка, на широком —
 *  правым полем. Без уборки после расширения одно и то же оказывалось на экране дважды.
 *  Снимаем слой и открываем ту же страницу адресом — человек остаётся, где был. */
function useSheetToPaneOnWide(section: UrlSection): React.MutableRefObject<string> {
  const opened = useRef('');
  const panel = useSlidePanel();
  const router = useRouter();
  useEffect(() => {
    // Порог и подписка — из одного источника раскладки (layout-mode.ts, ТЗ WEB-LOOK Э0.2).
    const onChange = () => {
      // Подстраница «Аккаунта» — горизонтальный шаг: в двух и трёх слоях живёт в поле
      // (ТЗ WEB-LOOK Э2.2), листом — только на телефоне.
      if (readLayoutMode() === 'phone' || !opened.current) return;
      const key = opened.current;
      opened.current = '';
      // Страница переезжает из листа в поле — лист снимается целиком, со всем, что над ним.
      panel.closeAll();
      router.replace(pickToPath(section, key));
    };
    return subscribeLayoutMode(onChange);
  }, [panel, router, section]);
  return opened;
}

/** Открыть страницу свода ШТОРКОЙ (узкий вид): страница-герой, как было у каждой
 *  строки настроек по отдельности — заголовок и подпись живут в самом герое. */
/** Адрес подстраницы «Аккаунта» для листа (ТЗ WEB-LOOK Э2.2, Закон 2): только у ключей,
 *  которые адрес знает в обе стороны (`pathToPick` ↔ `pickToPath`); у прочих листов
 *  («Настройки», диагностика) адреса нет — как и раньше. */
function accountHrefOf(key: string): { href: string; closeHref: string } | Record<string, never> {
  const href = pickToPath('account', key);
  if (pathToPick('account', href) !== key) return {};
  // ПРЯМОЙ ЗАХОД ПО ЭТОМУ ЖЕ АДРЕСУ — с его параметрами (FUNNEL-TZ §3): без них лист переписывал
  // адрес ссылки из сообщения (`?pay=1&k=…`) голым путём, и повторный вход по ссылке ломался.
  const here = typeof window === 'undefined' ? null : window.location;
  const withQuery = here && here.pathname === href && here.search ? `${href}${here.search}` : href;
  return { href: withQuery, closeHref: pickToPath('account', '') };
}

function useOpenSection(fromSettings = false): (s: SettingsSection) => void {
  const drawer = useSlidePanel();
  return (s: SettingsSection) =>
    drawer.open({
      // ИЗ «НАСТРОЕК» В ДВУХ СЛОЯХ — БЕЗ АДРЕСА «АККАУНТА» (ТЗ WEB-CAUSES М51, кадр desk-16): адрес
      // /app/account/<ключ> переключал оболочку на раздел «Аккаунт», и под панелью «Настройки»
      // подменялись «Аккаунтом». Панель — стопка над текущей страницей: адрес остаётся
      // /app/settings, «‹» просто снимает уровень. Телефон и приложение — как было (там
      // «Настройки» живут внутри вкладки «Аккаунт», адрес ей свой).
      ...(fromSettings && !isAppClient() && readLayoutMode() === 'two' ? {} : accountHrefOf(s.key)),
      // Страница-герой: на айфоне титул между кнопками пуст (владелец 2026-07-24).
      title: '',
      body: s.noHero ? (
        s.body
      ) : (
        <>
          <SectionHeroLive s={s} />
          {s.body}
        </>
      ),
      // Кнопка страницы (история платежей у «Подписки») живёт в шапке И при герое —
      // эталон «Сотрудники» (владелец 2026-08-09, IMG_1563).
      ...(s.action
        ? {
            actionIcon: s.action.icon,
            actionLabel: s.action.label,
            onAction: s.action.onClick,
            headerRight: s.action.dom,
          }
        : {}),
    });
}

/** ГЕРОЙ ОТКРЫТОЙ СТРАНИЦЫ — ЖИВОЙ К СМЕНЕ ЯЗЫКА (кадр iOS K07-locale-english, находка
 *  02.10): слова героя раньше отдавались строками в момент открытия, и после «English» на
 *  самой странице «Язык и регион» заголовок оставался русским до переоткрытия. Теперь герой
 *  перечитывает имя и подпись своего пункта из свода «Аккаунта» (тот же словарь, что
 *  подменяет locale-live); пункта там нет («Настройки») — прежние строки. */
function SectionHeroLive({ s }: { s: SettingsSection }) {
  const live = useAccountSections().find((x) => x.key === s.key);
  const src = live ?? s;
  return <SettingsHero icon={s.icon} title={src.title} {...(src.meta ? { meta: src.meta } : {})} color={s.color} />;
}

/** Открывалка справочника или «Сотрудников» — у них СВОИ шторки: у первых полноценный
 *  раздел с «+» и фильтром, у вторых стек экранов. Тела в своде рассчитаны на разворот,
 *  подменять их общей шторкой нельзя. */
function useOpenSectionSmart(): (s: SettingsSection) => void {
  const openData = useOpenDataSection();
  const openEmployees = useOpenEmployees();
  const openSection = useOpenSection(true);
  const openHelp = useOpenHelp();
  const openLegal = useOpenLegal();
  const openFolders = useOpenFolderSettings();
  const openBotsFolders = useOpenBotsFolders();
  return (s: SettingsSection) => {
    if (s.key === 'connections' || s.key === 'contacts' || s.key === 'products') return openData(s.key);
    if (s.key === 'employees') return openEmployees();
    // «Справка» — своей рукой: на телефоне в браузере слоем, иначе страницей справки.
    if (s.key === 'help') return openHelp();
    if (s.key === 'privacy' || s.key === 'terms') return openLegal(s.key);
    // «Папки → Чаты/Контакты» (П85): открывает САМ РАЗДЕЛ — теми же страницами, что его
    // шестерёнка в полосе папок. У «Ботов» страница своя и открывается обычной шторкой.
    // «Папки → Боты» на телефоне в браузере — тот же лист-стопка с ручкой, что шестерёнка полосы
    // «Ботов» (ТЗ WEB-CAUSES дополнение 396, кадр iOS B13-bots-folders-settings), не страница вбок.
    if (s.key === FOLDERS_ROW.bots && !isAppClient() && readLayoutMode() === 'phone') {
      openBotsFolders();
      return;
    }
    if (s.key === FOLDERS_ROW.messages || s.key === FOLDERS_ROW.contacts) {
      openFolders(s.key === FOLDERS_ROW.messages ? 'messages' : 'contacts');
      return;
    }
    openSection(s);
  };
}

/**
 * ОТКРЫТЬ НАСТРОЙКИ ПАПОК «ЧАТОВ» ИЛИ «КОНТАКТОВ» из «Настроек» (П85).
 *
 * В ПРИЛОЖЕНИИ у «Контактов» страницы папок живут в стопке САМИХ «Настроек» — их и
 * открываем прямо тут (тот же ход, что у шестерёнки «Контактов»). У «Чатов» полушторка
 * папок принадлежит вкладке «Чаты» — сперва переключаем вкладку, потом просим раздел.
 * В БРАУЗЕРЕ обе дороги одинаковы: уходим в раздел адресом и просим его открыть шторку.
 */
/** САЙТ: «Папки → Чаты/Контакты» открывают те же веб-шторки списка папок, что разделы,
 *  без перехода в раздел (П107/П108, владелец 13.09.2026). В приложении строки ведут в
 *  стопку настроек — см. useNativeSettingsPages. */
function useOpenFolderSettings(): (who: 'messages' | 'contacts') => void {
  const { data } = usePersistentData<{ ok: boolean; accounts: DialogAccount[] }>(
    'msg-accounts',
    '/api/cabinet/messages/accounts',
  );
  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const chats = useFoldersSettings(accounts, 'light');
  const contacts = useContactFoldersWeb(
    useMemo(
      () => accounts.map((a) => ({ value: a.key, label: a.name, hint: a.channel === 'tg' ? 'Telegram' : 'Instagram' })),
      [accounts],
    ),
  );
  return (who) => (who === 'messages' ? chats.open() : contacts.open());
}

/** Страница «Настройки». Пункты: справочники (Подключения · Контакты · Продукты ·
 *  Данные людей) ПЕРВЫМ пузырём — своего экрана «Данные» больше нет (владелец
 *  2026-08-07), вторым пузырём MCP-сервер · Сотрудники (· Кабинеты). */
export function SettingsPanel({
  acting = false,
  pick = null,
}: {
  acting?: boolean;
  /** Открытая страница раздела — она же кусок адреса (/app/settings/mcp). null — адрес
   *  указывает на корень раздела и колонки нет: показываем список пунктов. Ширину сюда
   *  НЕ передаём (владелец 15.08.2026): её замер живёт в эффекте, серверного кадра у него
   *  нет, и прямой заход по адресу страницы показывал список — см. shell.tsx. */
  pick?: string | null;
}) {
  const t = useTranslations('cabinet.settings');
  const tsec = useTranslations('cabinet.sections');
  const all = useAllSettingsSections(acting);
  const openSmartRaw = useOpenSectionSmart();
  const layoutMode = useLayoutMode();
  const phoneLook = usePhoneLook();
  const twoField = layoutMode === 'two' && !phoneLook && !isAppClient();
  const sheetOpenedRef = useSheetToPaneOnWide('settings');
  const openSmart = (s: SettingsSection) => {
    sheetOpenedRef.current = s.key;
    openSmartRaw(s);
  };

  // Широкий экран: выбранная слева страница открывается СПРАВА под общей шапкой
  // <PaneHead> (заголовок + кнопки страницы на одной линии).
  if (pick) {
    const p = all.find((x) => x.key === pick) ?? all[0];
    if (!p) return null;
    return (
      <PaneHead title={p.title}>
        {p.action ? <PageAction>{p.action.dom}</PageAction> : null}
        {p.body}
      </PaneHead>
    );
  }
  // СТРОКИ «Отправить журнал диагностики» БОЛЬШЕ НЕТ (владелец 14.09.2026): журнал
  // уезжает на сервер сам (DIAGNOSTICS-CANON), просить об этом человека незачем.
  return (
    <>
      {/* ДВА СЛОЯ (Д1.5, владелец 04.10.2026): поле — телефонный экран «Настройки» без нижнего меню
          (кадр iOS K09-settings): герой «шестерёнка · Настройки · подпись» над строками. На телефоне
          героя рисует открывающий лист (openSettingsSheet), здесь — сама страница. */}
      {twoField ? (
        <SettingsHero
          icon={<GearIcon />}
          title={tsec('settings')}
          meta={t('settingsMeta')}
          color={SETTINGS_COLOR}
        />
      ) : null}
      <SectionRows sections={all.filter((x) => x.key !== 'diagnostics')} open={openSmart} labels={{ 1: t('dataHead'), 2: t('foldersHead') }} />
    </>
  );
}

/** Цвет «Настроек» — один на плитку строки и на значок героя шторки (владелец
 *  29.08.2026): роль палитры --accent-stroke, своих оттенков не заводим. */
const SETTINGS_COLOR = 'var(--accent-stroke)';

// ─────────────── РОДНЫЕ ПОДСТРАНИЦЫ «Аккаунта» (этап Б плана) ───────────────
// Страницы «Тариф», «Язык и регион» и «Безопасность» в свежей сборке приложения рисует
// НАТИВ (AccountScreen.swift), а веб остаётся мозгом: собирает ГОТОВЫЕ строки теми же
// руками, что и веб-страницы (usePlanFacts, useSetLang, useTimezoneSetting, useFaceId,
// useTfaTelegram) — второго счёта и второй записи настройки нет.
//
// INVARIANT(scriptera): у ключа из этого набора веб-шторка в приложении БОЛЬШЕ НЕ
// ОТКРЫВАЕТСЯ (иначе поверх родной страницы встаёт её же веб-копия). Появилась родная
// страница — ключ сюда; убрал отсюда — вернулась шторка. Канон: docs/NATIVE-ACCOUNT-PLAN.md.
const NATIVE_PAGE_KEYS = new Set(['plan', 'locale', 'security', 'notifications', 'profile']);

/** РОДНЫЕ СТРАНИЦЫ «НАСТРОЕК» (этап Д) — БЕЗ АДРЕСА. Свод «Настроек» и его простые
 *  страницы («Кабинеты», «Диагностика») в приложении рисует натив, но кусок адреса им не
 *  положен: веб-шторка «Настроек» его тоже не меняла (её открывает строка «Аккаунта»,
 *  адрес остаётся /app/account). Поэтому стопку этих страниц веб держит у себя
 *  (nativeSettings в AccountPagesPanel) и отдаёт нативу тем же полем `stack` — сходимость
 *  адреса и стопки от этого не страдает, а «назад» не выносит человека из раздела.
 *
 *  INVARIANT(scriptera): ключ отсюда в приложении БОЛЬШЕ НЕ ОТКРЫВАЕТ веб-шторку (иначе
 *  поверх родной страницы встанет её же веб-копия). Канон: docs/NATIVE-ACCOUNT-PLAN.md.
 */
/** Ключ ведёт на родную страницу «Настроек»: пункт свода из набора ниже ИЛИ страница
 *  ВИДА (`page:…` — карточка человека, карточка доступа, форма подключения). Страниц
 *  видов по одной на вид: строка называет свою сама (NativeAccountField.page), поэтому
 *  каталог не пухнет от страницы на каждую запись. */
export function isNativeSettingsKey(key: string): boolean {
  return NATIVE_SETTINGS_KEYS.has(key) || key.startsWith('page:');
}

const NATIVE_SETTINGS_KEYS = new Set([
  'settings',
  'diagnostics',
  'cabinets',
  // Волна 2 (docs/NATIVE-TABS-PLAN.md): справочники и доступы — тоже родные страницы.
  // ГЛУБОКОЕ у них по-прежнему веб: карточка доступа и каталог «Подключить» (внешняя
  // авторизация — только веб-флоу), карточка человека, шторки MCP и «Сотрудников».
  'connections',
  'contacts',
  'mcp',
  'employees',
  // «Метки» (П190): список и листы добавления/переименования — родные страницы.
  ...TAGS_PAGE_KEYS,
]);

/** Ключ РОДНОЙ страницы «Подключить приложение» (волна 3). */
const MCP_CONNECT_PAGE = 'page:mcp-connect';

/**
 * РОДНАЯ СТРАНИЦА «MCP-СЕРВЕР» (волна 3 — аудит халявы): состав веб-страницы
 * (McpAppsPanel) один в один — что это такое над списком, у КАЖДОГО приложения свой
 * ЗНАК (McpAppMark: Claude, ChatGPT/Codex, Cursor, прочим — общий значок MCP),
 * отключение — значком ПИТАНИЯ с подтверждением, «Подключить приложение» — «+» В ШАПКЕ.
 * Сама шторка подключения — тоже родная страница (два способа с полями-копиями).
 * Данные и запись — ТОЙ ЖЕ рукой (useMcpApps), второй правды о подключениях нет.
 */
function useNativeMcpPage(
  hero: NativeAccountHero,
  stack: string[],
): { pages: NativeAccountPage[]; tapRow: (rowKey: string) => boolean } {
  const t = useTranslations('cabinet.settings');
  const tc = useTranslations('cabinet');
  const locale = useLocale();
  const open = stack.includes('settings');
  const { apps, loading, disconnect } = useMcpApps(open);
  const marks = useNativeMcpMarks(apps);

  const rows: NativeAccountField[] = apps.map((app) => {
    const dev = deviceLabel(app.userAgent);
    const seen = new Date(app.lastSeenAt).toLocaleDateString(locale);
    const connected = new Date(app.connectedAt).toLocaleDateString(locale);
    const mark = marks[app.familyId];
    return {
      key: `mcp:${app.familyId}`,
      kind: 'nav' as const,
      title: dev ? `${app.name} · ${dev}` : app.name,
      hint:
        t('mcpAppConnectedAt', { date: connected }) +
        (seen !== connected ? ` · ${t('mcpAppSeenAt', { date: seen })}` : ''),
      // Правый край — знак «питание», как в вебе: тап рвёт эту связь (с вопросом).
      mark: 'power',
      ...(mark?.logo ? { logo: mark.logo, logoColor: mark.color } : {}),
    };
  });

  const page: NativeAccountPage = {
    key: 'mcp',
    title: hero.title,
    hero,
    loading,
    // «Подключить приложение» — «+» В ШАПКЕ (владелец 2026-08-07), не кнопкой в пузыре.
    actions: [{ key: 'mcp:connect', icon: 'plus', label: t('mcpConnectRow'), page: MCP_CONNECT_PAGE }],
    groups: [
      // Что это такое — НАД списком, как в вебе (DHint над панелью).
      { key: 'mcp-intro', tone: 'hint', hint: t('mcpIntro'), fields: [] },
      {
        key: 'mcp-apps',
        label: t('mcpAppsHead'),
        ...(apps.length === 0 && !loading ? { hint: t('mcpAppsEmpty') } : {}),
        fields: rows,
      },
    ],
  };

  const connect = useNativeMcpConnectPage(stack.includes(MCP_CONNECT_PAGE));

  const tapRow = (rowKey: string): boolean => {
    const m = /^mcp:(.+)$/.exec(rowKey);
    if (!m || m[1] === 'connect') return false;
    const app = apps.find((a) => a.familyId === m[1]);
    if (!app) return true;
    // Разрыв доступа ИИ-приложения — с вопросом, как инлайн-подтверждение в вебе;
    // окно РОДНОЕ (то же askNativeConfirm, что у «Выйти»).
    if (!hasNativeConfirm()) {
      disconnect(app.clientId, app.familyId);
      return true;
    }
    void askNativeConfirm({
      question: t('mcpAppDisconnectQ'),
      confirmLabel: t('mcpAppDisconnect'),
      cancelLabel: tc('cancel'),
      destructive: true,
    }).then((yes) => {
      if (yes) disconnect(app.clientId, app.familyId);
    });
    return true;
  };

  return { pages: [page, connect.page], tapRow };
}

/** ЗНАКИ ПОДКЛЮЧЁННЫХ ПРИЛОЖЕНИЙ ДЛЯ РОДНОЙ СТРАНИЦЫ — тем же выбором, что McpAppMark
 *  выше (Claude / OpenAI / Cursor / общий значок MCP) и тем же цветом: у сборки этих
 *  знаков нет, картинку готовит веб общим конвейером. */
function useNativeMcpMarks(apps: McpApp[]): Record<string, { logo?: string; color: string }> {
  const dark = useIsDark();
  const asks: NativeIconAsk[] = [];
  const tints: Record<string, string> = {};
  const ink = readRoleHex('--ink', '#16161a');
  const mut = readRoleHex('--mut', '#8a8a8e');
  for (const app of apps) {
    const name = app.name;
    if (/claude/i.test(name)) {
      tints[app.familyId] = '#e07a68';
      asks.push({
        key: app.familyId,
        node: (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d={CLAUDE_PATH} />
          </svg>
        ),
        color: '#e07a68',
      });
      continue;
    }
    if (/chatgpt|openai|codex|gpt/i.test(name)) {
      tints[app.familyId] = ink;
      asks.push({ key: app.familyId, src: dark ? OPENAI_ICON_DARK : OPENAI_ICON_LIGHT, color: ink });
      continue;
    }
    if (/cursor/i.test(name)) {
      tints[app.familyId] = ink;
      asks.push({
        key: app.familyId,
        node: (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d={CURSOR_PATH} />
          </svg>
        ),
        color: ink,
      });
      continue;
    }
    tints[app.familyId] = mut;
    asks.push({ key: app.familyId, node: <McpIcon />, color: mut });
  }
  const pngs = useNativeIcons(asks);
  const out: Record<string, { logo?: string; color: string }> = {};
  for (const [id, color] of Object.entries(tints)) {
    out[id] = pngs[id] ? { logo: pngs[id], color } : { color };
  }
  return out;
}

/** РОДНАЯ СТРАНИЦА «ПОДКЛЮЧИТЬ ПРИЛОЖЕНИЕ» — состав веб-шторки (McpConnectSheet)
 *  построчно: предупреждение «сервер ещё не готов», два способа пузырями со своими
 *  названиями и описаниями «для кого», внутри — поля-копии с подписями, куда их
 *  вставлять. Тап по полю кладёт значение в буфер (то же, что кнопка копирования). */
function useNativeMcpConnectPage(open: boolean): { page: NativeAccountPage } {
  const t = useTranslations('cabinet.settings');
  // sensitive: токен MCP — секрет, живёт только in-memory (не localStorage).
  const { data: info, loading } = usePersistentData<McpInfo>('cabinet:mcp', open ? '/api/cabinet/mcp' : '', {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
    sensitive: true,
  });

  const groups: NativeAccountGroup[] = [];
  if (!loading && !info) {
    groups.push({ key: 'mcp-none', tone: 'hint', hint: t('mcpUnavailable'), fields: [] });
  } else if (info) {
    if (!info.ready) {
      groups.push({
        key: 'mcp-notready',
        tone: 'warn',
        toneColor: readRoleHex('--amber', '#d99000'),
        hint: t('mcpNotReady'),
        fields: [],
      });
    }
    groups.push({
      key: 'mcp-way1',
      label: t('mcpWay1Title'),
      hint: t('mcpWay1Sub'),
      fields: [
        {
          key: 'mcp:c:url1',
          kind: 'copy',
          title: t('mcpUrlLabel'),
          value: info.mcpUrl,
          hint: t('mcpOauthHint'),
        },
      ],
    });
    groups.push({
      key: 'mcp-way2',
      label: t('mcpWay2Title'),
      hint: t('mcpWay2Sub'),
      fields: [
        {
          key: 'mcp:c:url2',
          kind: 'copy',
          title: t('mcpUrlLabel'),
          value: info.mcpUrl,
          hint: t('mcpKeyUrlHint'),
        },
        {
          key: 'mcp:c:token',
          kind: 'copy',
          title: t('mcpTokenLabel'),
          value: info.token,
          hint: t('mcpKeyHeaderHint'),
        },
      ],
    });
  }

  const page: NativeAccountPage = {
    key: MCP_CONNECT_PAGE,
    title: t('mcpConnectRow'),
    hero: { icon: 'sparkles', color: accountRoleHex('var(--teal)'), title: t('mcpConnectRow') },
    loading: loading && !info,
    // КОРОТКИЙ ВВОД, ПОСЛЕ КОТОРОГО ОСТАЁШЬСЯ НА МЕСТЕ (Н39, словарь §11.0): скопировал
    // адрес и ключ — и обратно в «Настройки». Вертикальная шторка, фон под ней стоит.
    present: 'sheet',
    groups,
  };
  return { page };
}

/** Сборщик родных страниц «Настроек»: свод (те же пункты, что у веб-страницы — с учётом
 *  спрятанных и чужого кабинета), «Диагностика», «Кабинеты» и справочники волны 2
 *  (Подключения, Контакты, MCP, Сотрудники). Строки свода — вид `nav` с ПЛИТКОЙ значка
 *  (как в вебе): есть у ключа родная страница — натив откроет её сам, нет — тап уходит
 *  вебу и тот показывает прежнюю шторку раздела.
 *
 *  Состав берём ЕДИНСТВЕННОЙ рукой свода (useAllSettingsSections) — второго списка
 *  разделов «Настроек» в кабинете нет.
 *
 *  `stack` — что открыто прямо сейчас: пока свод «Настроек» не открыт, страницы справочников
 *  в сеть НЕ ходят (кэш кабинета показывают, запросов не делают). */
function useNativeSettingsPages(
  settingsTitle: string,
  stack: string[],
): {
  pages: NativeAccountPage[];
  tapRow: (key: string) => boolean;
  applySet: (key: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('cabinet.settings');
  const tsec = useTranslations('cabinet.sections');
  const ts = useTranslations('cabinet.staff');
  // Чужой кабинет (роль «Сотрудник») сюда не доходит вовсе: в нём вкладку рисует
  // веб-страница «Настроек» (tab-stack), родного раздела «Аккаунт» нет — значит и
  // свода этого нет. Поэтому acting здесь всегда false.
  const sections = useAllSettingsSections();
  const openSection = useOpenSectionSmart();
  const diag = useSendDiagnostics();
  const cabinetsRow = sections.find((x) => x.key === 'cabinets');
  const cabinets = useNativeCabinetsPage(cabinetsRow?.title ?? '', cabinetsRow?.meta ?? '');
  const diagState = diag.state;
  // ГЕРОИ СТРАНИЦ ВОЛНЫ 2 — ОДНОЙ РУКОЙ: подпись и цвет берём у строки свода (там они
  // уже есть), а если строка спрятана (MCP и «Сотрудники» — hidden-features), у страницы
  // остаются её собственные слова. Второй таблицы значков нет: она в account-native.ts.
  const heroFor = (key: string, title: string, meta: string, color: string): NativeAccountHero => {
    const row = sections.find((x) => x.key === key);
    return {
      icon: accountRowIcon(key),
      color: accountRoleHex(row?.color ?? color),
      title: row?.title ?? title,
      meta: row?.meta ?? meta,
    };
  };
  // В сеть за справочниками ходим, только когда свод открыт (человек в «Настройках»):
  // это решает каждая страница сама по СТОПКЕ — ей же она узнаёт, открыта ли её
  // глубокая страница (карточка человека, форма подключения) и нужны ли ей данные.
  // У «Подключений» и «Контактов» подписи в своде нет — и герой обходится одним
  // названием: своих слов ради красоты не сочиняем.
  const conns = useNativeConnectionsPage(heroFor('connections', tsec('connections'), '', 'var(--green)'), stack);
  const people = useNativeContactsPage(heroFor('contacts', tsec('contacts'), '', 'var(--blue)'), stack);
  const mcp = useNativeMcpPage(heroFor('mcp', t('mcpRow'), t('mcpMeta'), 'var(--teal)'), stack);
  // У «Сотрудников» подпись героя — РАЗВЁРНУТАЯ, как у веб-шторки (владелец 2026-07-24),
  // а не короткая строка свода.
  const staff = useNativeEmployeesPage(
    { ...heroFor('employees', ts('employeesRow'), '', 'var(--violet)'), meta: ts('employeesHeroSub') },
    stack,
  );
  // «Папки → Боты» (П85): категории ботов — своя страница стопки «Настроек».
  const botFolders = useNativeBotsFoldersPages(stack);
  const chatFolders = useNativeChatFoldersPages(stack);
  // «Данные → Метки» (П190): список, листы поля, вопросы wizAsk.
  const tagsPages = useNativeTagsPages(stack);

  // «Диагностика» — НЕ строка свода (владелец 26.08.2026): в вебе она стоит светлым мелким
  // текстом-ссылкой ПОД списком, и в приложении обязана стоять там же (аудит халявы, волна 3).
  const rows = sections
    .filter((s) => s.key !== 'diagnostics')
    .map((s) => ({ key: s.key, title: s.title, color: s.color, group: s.group }));
  const hasCabinets = !!cabinetsRow;
  const rowsKey = JSON.stringify(rows);
  const cabinetsPage = cabinets.page;

  const pages = useMemo<NativeAccountPage[]>(() => {
    const groups: NativeAccountGroup[] = [1, 2, 3, 4, 5]
      .filter((g) => rows.some((r) => r.group === g))
      .map((g) => ({
        key: `settings${g}`,
        // ЗАГОЛОВОК ГРУППЫ «Данные» (владелец 09.09.2026) — над пузырём справочников
        // (Подключения, Контакты); сюда же позже встанут данные пользователя. Второй
        // пузырь — «Папки» (владелец 13.09.2026, П85). У остальных заголовка нет.
        ...(g === 1 ? { label: t('dataHead') } : g === 2 ? { label: t('foldersHead') } : {}),
        fields: rows
          .filter((r) => r.group === g)
          .map(
            (r): NativeAccountField => ({
              key: r.key,
              kind: 'nav',
              title: r.title,
              icon: accountRowIcon(r.key),
              color: accountRoleHex(r.color),
            }),
          ),
      }));
    // КНОПКИ «ОТПРАВИТЬ ЖУРНАЛ ДИАГНОСТИКИ» БОЛЬШЕ НЕТ (владелец 14.09.2026: «журнал
    // теперь не нужен кнопкой, потому что мы и так всё можем»). Журнал уезжает на сервер
    // САМ — при перезапуске, уходе родного экрана и по сторожу (DIAGNOSTICS-CANON), и
    // просить человека нажимать кнопку незачем.
    const out: NativeAccountPage[] = [
      {
        key: 'settings',
        title: settingsTitle,
        loading: groups.length === 0,
        groups,
      },
    ];
    // «Кабинеты» — только когда их правда больше одного (пункт свода есть).
    if (hasCabinets) out.push(cabinetsPage);
    return out;
    // rowsKey — отпечаток строк свода: пересобираем страницу, только когда состав или
    // подписи правда изменились (смена языка, пришли кабинеты), а не каждый рендер.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey, settingsTitle, t, diagState, hasCabinets, cabinetsPage]);

  const tapRow = (key: string): boolean => {
    // «Метки» (П190): строки меток, «Добавить метку», пункты долгого нажатия. Строку свода
    // «Метки» рука только отмечает в журнале — страницу открывает натив (она в каталоге).
    if (tagsPages.tapRow(key)) return true;
    // «Папки» (П85): «Боты» — страница стопки «Настроек» (её кладёт сам веб: натив ключа
    // ещё не знает), «Чаты» и «Контакты» открывают свои разделы.
    if (key === FOLDERS_ROW.bots) {
      botFolders.tapRow(BT_ROW.open);
      pushNativeSettingsPage(`${BT_FOLDERS_PAGE}list`);
      return true;
    }
    // «Чаты» и «Контакты» — страницы папок ПРЯМО В СТОПКЕ настроек (П107/П108, владелец
    // 13.09.2026): без смены вкладки и без страницы «Контакты».
    if (key === FOLDERS_ROW.messages) {
      chatFolders.openList();
      return true;
    }
    if (key === FOLDERS_ROW.contacts) {
      openNativeSettingsPage(['settings', `${CT_FOLDERS_PAGE}list`]);
      return true;
    }
    if (chatFolders.tapRow(key)) return true;
    if (botFolders.tapRow(key)) return true;
    if (cabinets.tapRow(key)) return true;
    // Строки страниц волны 2 (доступ, человек, приложение, сотрудник) разбирают ИХ ЖЕ
    // руки — они же открывают прежние веб-шторки. Не их ключ — уходит дальше.
    if (conns.tapRow(key) || people.tapRow(key) || mcp.tapRow(key) || staff.tapRow(key)) return true;
    // Родная страница у ключа есть — её откроет натив, а веб только запомнит стопку
    // (это делает свод раздела); значит строка не наша.
    if (isNativeSettingsKey(key)) return false;
    const s = sections.find((x) => x.key === key);
    if (!s) return false;
    // Прежняя веб-шторка раздела: справочник — своим полноценным разделом,
    // «Сотрудники» — своим стеком экранов, остальное — общей шторкой.
    openSection(s);
    return true;
  };

  /** Набранное в родном поле поиска и выбор в родном листе отбора — отбирает ВЕБ:
   *  каждая страница разбирает СВОИ ключи, своей логики отбора у натива нет. */
  const applySet = (key: string, value: string | boolean): boolean =>
    conns.applySet(key, value) ||
    people.applySet(key, value) ||
    staff.applySet(key, value) ||
    chatFolders.applySet(key, value) ||
    botFolders.applySet(key, value) ||
    tagsPages.applySet(key, value);

  // К своду и его простым страницам добавляем справочники волны 2 — их собрали свои же
  // руки (рядом с настройкой, а не тут). Новый объект каждый рендер не страшен: нативу
  // уезжает только настоящая разница (сверка по содержимому в useNativeAccount).
  return {
    pages: [...pages, ...conns.pages, ...people.pages, ...mcp.pages, ...staff.pages, ...botFolders.pages, ...chatFolders.pages, ...tagsPages.pages],
    tapRow,
    applySet,
  };
}

/** Сборщик родных подстраниц: `pages` — каталог (натив открывает страницу мгновенно, не
 *  дожидаясь круга к вебу), `applySet` — запись значения по ключу строки, `tapRow` —
 *  нажимаемая строка страницы (веб открывает свою шторку; вернул false — ключ не наш).
 *  `stack` — что открыто по адресу: тяжёлый список поясов (сотни рядов) едет мостом
 *  ТОЛЬКО пока страница открыта, чтобы каждый патч не таскал его зря. */
function useNativeAccountPages(
  sections: SettingsSection[],
  stack: string[],
  user: ProfileInfo,
  /** Фото героя — то же, что у корня раздела (готовое, из avatarTabPng). */
  heroPhoto: string,
  /** Готовое название «Настроек» — титул их родного свода (этап Д). */
  settingsTitle: string,
): {
  pages: NativeAccountPage[];
  applySet: (key: string, value: string | boolean) => void;
  tapRow: (key: string) => boolean;
  onPhoto: (data: string, cropped?: boolean) => void;
} {
  const t = useTranslations('cabinet.settings');
  const tb = useTranslations('cabinet.billing');
  const fmtDate = useFmtDate();
  // ЯЗЫК: `lang` — ВЫБРАННЫЙ (галочка переезжает тем же кадром, до приезда словаря),
  // `langFailed` — запись не прошла, язык вернулся (П12, владелец 11.09.2026).
  const { lang, setLang, failed: langFailed } = useSetLang();
  const plan = usePlanFacts();
  const tz = useTimezoneSetting();
  const faceId = useFaceId();
  const tfa = useTfaTelegram();
  const geo = useGeoSetting();
  const localeOpen = stack.includes('locale');

  const title = (key: string) => sections.find((x) => x.key === key)?.title ?? '';
  const meta = (key: string) => sections.find((x) => x.key === key)?.meta ?? '';
  /** Цвет роли строки свода — нужен герою страницы, когда она шлёт его САМА (общий
   *  механизм героя достраивает его только страницам без своего). */
  const rowColor = (key: string) => sections.find((x) => x.key === key)?.color ?? 'var(--accent-stroke)';
  // Страницы «Уведомления» и «Профиль» собирают их собственные руки — там же, где живут
  // настройки и запись (notifications.tsx, profile.tsx): второй правды о них тут нет.
  const notify = useNativeNotifyPage('notifications', title('notifications'), meta('notifications'), stack);
  const profile = useNativeProfilePage(user, heroPhoto, title('profile'));
  // Свод «Настроек» и его простые страницы (этап Д) — своей рукой; герой свода
  // достраивается общим механизмом по строке «Настройки» самого раздела.
  const settings = useNativeSettingsPages(settingsTitle, stack);
  const planTitle = title('plan');
  const localeTitle = title('locale');
  const securityTitle = title('security');
  // Цвет героя «Тарифа» — ролью его строки свода; считаем ЗДЕСЬ, чтобы у сборщика
  // страниц зависимостью была строка, а не новая рука на каждый рендер.
  const planColor = accountRoleHex(rowColor('plan'));
  const faceIdOn = faceId.state?.enabled;
  const tzOptions = tz.options;
  const tzValue = tz.value;

  const pages = useMemo<NativeAccountPage[]>(() => {
    const out: NativeAccountPage[] = [];
    // ── «Тариф»: ТОЛЬКО ФАКТЫ. Ни цены, ни намёка, где платить (CLAUDE.md запрет №7).
    // СТРОКА = «ЧТО — ЗНАЧЕНИЕ» (владелец 09.09.2026): подписи под названием строки нет
    // ни у одной — «Активен до» стало ОТДЕЛЬНОЙ строкой, а не абзацем под «Тарифом»
    // (канон Apple). На тарифе без срока (бесплатный) этой строки нет вовсе.
    const planFields: NativeAccountField[] = [
      { key: 'plan', kind: 'value', title: tb('planLabel'), value: plan.planName },
      ...(plan.until
        ? [
            {
              key: 'planUntil',
              kind: 'value' as const,
              title: tb('planUntilLabel'),
              value: fmtDate(plan.until),
            },
          ]
        : []),
      { key: 'planAccounts', kind: 'value', title: tb('kvAccounts'), value: tb('accountsN', { n: plan.accountsN }) },
      // INVARIANT(scriptera): в приложении на «Тарифе» НЕТ строк «Автопродление» и «Карта» и их
      // шторок. Причина: это платёжное (карта, списания) — Apple снимает приложение за оплату мимо
      // своих покупок; владелец 14.10.2026 «нарушил правило и выложил оплату в айос». Управление
      // автопродлением и картой — только на вебе («Подписка»). Канон: CLAUDE.md, позиционирование п.7.
    ];
    out.push({
      key: 'plan',
      title: planTitle,
      // ГЕРОЙ — СВОЙ (владелец 09.09.2026): описание героя говорит то, что раньше стояло
      // сноской под пузырём, и сноски больше нет.
      hero: {
        icon: accountRowIcon('plan'),
        color: planColor,
        title: planTitle,
        meta: tb('appPlanLocked'),
      },
      // Сводка ещё едет — страница ждёт её пустым пузырём, а не показывает «Бесплатно»
      // и не перескакивает на настоящий тариф (дёрганья запрещены).
      loading: !plan.ready,
      groups: [{ key: 'plan', fields: plan.ready ? planFields : [] }],
    });

    // ── «Язык и регион»: язык (живая смена) и часовой пояс — родными листами выбора.
    out.push({
      key: 'locale',
      title: localeTitle,
      groups: [
        // ЯЗЫК — ВЫБОРОМ НА САМОЙ СТРАНИЦЕ (аудит халявы, волна 3): в вебе это раскрытый
        // пузырь выбора (FChoiceBlock), а не строка-лист. Языки подписаны САМИ СОБОЙ в
        // обеих локалях — переводить нечего.
        // СНОСКИ ПОД ПУЗЫРЁМ НЕТ (владелец 09.09.2026): languageMeta «Интерфейс, письма и
        // уведомления» читалась как заголовок следующего пузыря и ломала отступы.
        {
          key: 'lang',
          label: t('language'),
          fields: [
            // ГАЛОЧКА — ПО ВЫБРАННОМУ ЯЗЫКУ, А НЕ ПО ДОЕХАВШЕМУ СЛОВАРЮ (владелец
            // 11.09.2026, П12): словарь грузится чанком, и до его приезда useLocale
            // отдавал прежний язык — галочки стояли у обоих.
            { key: 'lang:ru', kind: 'check', radio: true, title: 'Русский', on: lang !== 'en' },
            { key: 'lang:en', kind: 'check', radio: true, title: 'English', on: lang === 'en' },
          ],
          // Запись не прошла — язык вернулся, и человек об этом знает (П12).
          ...(langFailed ? { tone: 'danger' as const, hint: t('languageError') } : {}),
        },
        {
          key: 'locale',
          // ЗАГОЛОВОК «Регион» НАД ПУЗЫРЁМ (владелец 09.09.2026, обход после сборки 173):
          // у соседнего пузыря заголовок есть («Язык»), и без него этот стоял впритык —
          // в сгруппированном списке Apple либо заголовки у всех групп, либо ни у одной.
          label: t('region'),
          // ЧТО ДЕЛАЕТ ПОЯС — СНОСКОЙ ПОД ПУЗЫРЁМ, А НЕ АБЗАЦЕМ ПОД НАЗВАНИЕМ СТРОКИ
          // (владелец 09.09.2026): строка остаётся «Часовой пояс — <город> ›».
          hint: t('timezoneNote'),
          fields: [
            // СТРАНА — первой строкой «Региона» (О5): тот же родной выбор с поиском, что у
            // пояса. Валюты на родной странице нет никогда (инвариант geo-field.tsx).
            geoNativeField({
              country: geo.country,
              locale: lang === 'en' ? 'en' : 'ru',
              open: localeOpen,
              title: t('profileCountry'),
              none: t('profileCountryNone'),
              search: t('profileCountrySearch'),
            }),
            {
              key: 'tz',
              kind: 'choice',
              title: t('timezone'),
              // Подсказка под строкой — только СОСТОЯНИЕ (пояс не определился, не удалось
              // записать); в обычном случае подписи под названием нет.
              ...(tz.failed ? { hint: t('timezoneError') } : tz.unknown ? { hint: t('timezoneUnknown') } : {}),
              value: tzValue ? zoneLabel(tzValue) : t('timezoneEmpty'),
              picked: tzValue,
              ...(localeOpen ? { options: tzOptions.map((o) => ({ value: o.value, label: o.label, hint: o.desc })) } : {}),
              search: t('timezoneSearchPh'),
            },
          ],
        },
      ],
    });

    // ── «Безопасность»: состав ТОТ ЖЕ, что у веб-шторки (SecuritySheet).
    // ДВЕ ГРУППЫ ПО СМЫСЛУ, У КАЖДОЙ СВОЙ ЗАГОЛОВОК (владелец 09.09.2026): «Каждое
    // открытие» — Face ID при запуске приложения, «Вход с нового устройства» —
    // подтверждение в Telegram. Прежний общий пузырь с именем securityMeta отменён этим
    // решением; веб-шторка приведена к тем же двум группам, экраны совпадают.
    // У КАЖДОГО ПУЗЫРЯ ЕЩЁ И СНОСКА ПОД НИМ (владелец 09.09.2026, обход после сборки
    // 173): заголовок называет случай, сноска говорит, когда именно спросят. Подсказки-
    // состояния («Сначала привяжите Telegram») остаются при своей строке.
    const guardGroups: NativeAccountGroup[] = [];
    if (faceIdOn !== undefined) {
      guardGroups.push({
        key: 'sec-open',
        label: t('secEveryOpen'),
        hint: t('faceIdNote'),
        fields: [{ key: 'faceId', kind: 'switch', title: t('faceId'), on: faceIdOn }],
      });
    }
    if (tfa.ready) {
      guardGroups.push({
        key: 'sec-device',
        label: t('secNewDevice'),
        hint: t('tfaTgNote'),
        fields: [
          {
            key: 'tfaTelegram',
            kind: 'switch',
            title: t('tfaTg'),
            on: tfa.on,
            disabled: !tfa.usable,
            ...(tfa.meta ? { hint: tfa.meta } : {}),
          },
        ],
      });
    }
    out.push({
      key: 'security',
      title: securityTitle,
      loading: guardGroups.length === 0,
      groups: guardGroups,
    });
    return out;
  }, [
    t,
    tb,
    fmtDate,
    lang,
    langFailed,
    localeOpen,
    plan.ready,
    plan.planName,
    plan.until,
    plan.accountsN,
    planTitle,
    planColor,
    localeTitle,
    securityTitle,
    tzValue,
    tzOptions,
    tz.failed,
    geo.country,
    tz.unknown,
    faceIdOn,
    tfa.ready,
    tfa.on,
    tfa.usable,
    tfa.meta,
  ]);

  const applySet = (key: string, value: string | boolean) => {
    // Радио-строка языка: тапнули — ставим ЕЁ язык (снять язык нельзя, снятого языка не
    // бывает), запись — прежней рукой useSetLang.
    if (key === 'lang:ru' || key === 'lang:en') return setLang(key === 'lang:en' ? 'en' : 'ru');
    if (key === 'tz' && typeof value === 'string') return tz.save(value);
    if (key === GEO_COUNTRY_KEY && typeof value === 'string') return geo.saveCountry(value);
    if (key === 'faceId' && typeof value === 'boolean') return faceId.toggle(value);
    if (key === 'tfaTelegram' && typeof value === 'boolean') return tfa.toggle();
    if (notify.applySet(key, value)) return;
    if (settings.applySet(key, value)) return;
    profile.applySet(key, value);
  };
  const tapRow = (key: string): boolean => {
    return notify.tapRow(key) || profile.tapRow(key) || settings.tapRow(key);
  };
  // Каталог страниц: к трём собранным здесь добавляем «Уведомления», «Профиль» и свод
  // «Настроек» с его страницами — их собрали свои же руки. Новый объект на каждый рендер
  // не страшен: нативу уезжает только настоящая разница (сверка по содержимому в
  // useNativeAccount).
  return {
    pages: [...pages, ...notify.pages, ...profile.pages, ...settings.pages],
    applySet,
    tapRow,
    onPhoto: profile.onPhoto,
  };
}

/** Страница раздела «Аккаунт» (владелец 2026-08-07 — ГЛАВНЫЙ пункт меню).
 *  Узкий вид: сверху ГЕРОЙ профиля (фото, имя, почта), ниже личные страницы пузырями по
 *  блокам, отдельным пузырём — вход в «Настройки» (рейла на телефоне нет).
 *  Широкий: список стоит слева в колонке, справа — выбранная страница; корень раздела
 *  (нажали «Аккаунт») открывает ПРОФИЛЬ (владелец 2026-08-10), а «Настройки» — свой
 *  пункт рейла, из страницы он убран. */
export function AccountPagesPanel({
  user,
  wide = false,
  pick = null,
}: {
  user: ProfileInfo;
  /** Есть вторая колонка: список пунктов стоит слева, тут — только выбранная страница
   *  (или корень раздела с героем). */
  wide?: boolean;
  /** Выбранная страница; пусто — корень раздела. */
  pick?: string | null;
}) {
  const t = useTranslations('cabinet');
  const drawer = useSlidePanel();
  const sections = useAccountSections(user);
  const openPage = useOpenSection();
  const openProfile = useOpenProfile(user);
  // «Справка» из корня — той же рукой, что открывала её из «Настроек»: родные экраны
  // справки (водитель на этом же разделе), а без них — прежняя лестница листов.
  const openHelp = useOpenHelp();
  const openLegal = useOpenLegal();
  const native = useNativeApp();
  // Узкий вид: «Профиль» открывается СВОЕЙ шторкой (фото вместо героя, кроппер и
  // передача кабинета слоями поверх) — той же, что кружок шапки; общий герой страницы
  // ему не нужен. Переключатели (тема, язык) не открываются вовсе — они уже строки.
  const sheetOpenedRef = useSheetToPaneOnWide('account');
  // ── СТОПКА РОДНЫХ ПОДСТРАНИЦ (этап Б) ── ЧТО ОТКРЫТО, РЕШАЕТ АДРЕС: /app/account/<key>.
  // Натив открывает страницу пальцем мгновенно и лишь СООБЩАЕТ об этом (accountTap), а
  // веб чинит адрес — поэтому стопка и адрес всегда сходятся, а тап не ждёт круга к вебу.
  const router = useRouter();
  // Стопка страниц «Настроек» — БЕЗ адреса (см. NATIVE_SETTINGS_KEYS): её держит веб у
  // себя и отдаёт нативу тем же полем `stack`, следом за страницей из адреса. Открывает
  // страницу палец (натив ставит её мгновенно), а веб лишь ЗАПОМИНАЕТ, что открыто, —
  // тогда «назад» и сверка стопки сходятся без круга к адресу.
  const [nativeSettings, setNativeSettings] = useState<string[]>([]);
  // СТОПКА ПЕРЕЖИВАЕТ СМЕНУ ВКЛАДКИ (владелец 10.09.2026, канон Apple; отменяет решение
  // 03.09 «уход из раздела снимает подстраницы»). Вкладка помнит, где человек был: ушёл из
  // «Контактов» в «Чаты», вернулся в «Аккаунт» — он в «Контактах». Решение 03.09 лечило
  // симптом (над подстраницей гас хром корня для ВСЕГО приложения, и на «Ботах» пропадали
  // кружки) — настоящая причина закрыта в оболочке: хром корня гасится только на видимой
  // вкладке «Аккаунт». Побочный эффект старого решения — при возврате была видна
  // анимация снятия страниц (видео владельца 10.09, 08:29) — ушёл вместе с ним.
  // Пока адрес вне раздела, нативу уходит ПРЕЖНЯЯ стопка: страница из последнего адреса
  // раздела (`heldPick`) плюс память «Настроек». До корня стопку сворачивает ТОЛЬКО
  // повторный тап по активной вкладке «Аккаунт» (событие оболочки `scr-tab-root`,
  // shell.tsx): адрес уходит на корень раздела, память «Настроек» стирается — натив
  // снимает страницы с анимацией, потому что вкладка видна.
  // СТАРЫЕ СБОРКИ (без метода accountStackKeep) хром по вкладке не считают — им остаётся
  // поведение 03.09: вне раздела стопка пустая, память «Настроек» стирается при возврате.
  const pathname = usePathname();
  const inAccount = /\/app\/account(?:\/|$)/.test(pathname ?? '');
  const keepStack = nativeMethod('accountStackKeep');
  const heldPickRef = useRef<string | null>(pick);
  if (inAccount) heldPickRef.current = pick;
  const heldPick = inAccount ? pick : keepStack ? heldPickRef.current : null;
  const wasInRef = useRef(inAccount);
  useEffect(() => {
    if (keepStack) return;
    if (!wasInRef.current && inAccount) setNativeSettings([]);
    wasInRef.current = inAccount;
  }, [inAccount, keepStack]);
  useEffect(() => {
    const onRoot = (e: Event) => {
      if ((e as CustomEvent<{ key?: string }>).detail?.key !== 'account') return;
      heldPickRef.current = null;
      setNativeSettings([]);
    };
    window.addEventListener('scr-tab-root', onRoot);
    return () => window.removeEventListener('scr-tab-root', onRoot);
  }, []);
  const stack = useMemo(
    () =>
      inAccount || keepStack
        ? [...(heldPick && NATIVE_PAGE_KEYS.has(heldPick) ? [heldPick] : []), ...nativeSettings]
        : [],
    [inAccount, keepStack, heldPick, nativeSettings],
  );
  /** Палец открыл родную страницу «Настроек» — запомнить (новый список, не мутация). */
  const pushNativeSettings = (key: string) => {
    setNativeSettings((cur) => (cur[cur.length - 1] === key ? cur : [...cur, key]));
  };
  // ПРОСЬБА ПОЛОЖИТЬ ОДНУ СТРАНИЦУ (П10в, владелец 11.09.2026): страницы, которой ещё
  // нет в каталоге, натив не находит и тап проглатывает — тогда ключ в стопку кладёт сам
  // веб (pushNativeSettingsPage), и страница приезжает тем же патчем, что стопка.
  useEffect(() => {
    const on = (e: Event) => {
      const key = (e as CustomEvent<{ key?: string }>).detail?.key ?? '';
      if (key) setNativeSettings((cur) => (cur[cur.length - 1] === key ? cur : [...cur, key]));
    };
    window.addEventListener(NATIVE_SETTINGS_PUSH_EVENT, on);
    return () => window.removeEventListener(NATIVE_SETTINGS_PUSH_EVENT, on);
  }, []);
  // ПРОСЬБА ИЗВНЕ (волна 3): адрес /app/connections перехвачен и просит открыть родную
  // страницу свода. Стопку ставим целиком — «Настройки» и нужную страницу.
  useEffect(() => {
    const take = () => {
      const keys = takePendingSettingsStack();
      if (keys) {
        setNativeSettings(keys);
      }
    };
    take();
    const on = () => take();
    window.addEventListener(NATIVE_SETTINGS_OPEN_EVENT, on);
    return () => window.removeEventListener(NATIVE_SETTINGS_OPEN_EVENT, on);
  }, []);
  /** Ушли назад с такой страницы: снимаем её и всё, что выше неё. */
  const popNativeSettings = (key: string) =>
    setNativeSettings((cur) => {
      const i = cur.indexOf(key);
      return i < 0 ? cur : cur.slice(0, i);
    });
  // ПРОСЬБА ЗАКРЫТЬ СТРАНИЦУ ИЗ ВЕБА (Н46): карточка аккаунта после «✓»/удаления и форма
  // нового подключения после успеха просят снять себя — тем же движением, что «назад».
  useEffect(() => {
    const on = (e: Event) => {
      const key = (e as CustomEvent<{ key?: string }>).detail?.key;
      if (key) popNativeSettings(key);
    };
    window.addEventListener(NATIVE_SETTINGS_POP_EVENT, on);
    return () => window.removeEventListener(NATIVE_SETTINGS_POP_EVENT, on);
    // popNativeSettings — стабильная рука над setState, подписка ставится один раз.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ПРОСЬБА ПОДМЕНИТЬ СТРАНИЦУ (О5.2): подключили аккаунт — форма уступает место вопросу
  // «расскажите о себе». Снятие и подкладывание идут ОДНИМ движением: двумя событиями
  // между ними мелькал бы пустой кадр «Подключений».
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ from?: string; to?: string }>).detail;
      if (!d?.from || !d.to) return;
      setNativeSettings((cur) => {
        const i = cur.indexOf(d.from!);
        return i < 0 ? cur : [...cur.slice(0, i), d.to!];
      });
    };
    window.addEventListener(NATIVE_SETTINGS_REPLACE_EVENT, on);
    return () => window.removeEventListener(NATIVE_SETTINGS_REPLACE_EVENT, on);
  }, []);
  /** Страницу в стопку положили МЫ — значит «назад» это шаг истории назад. Заход прямой
   *  ссылкой (письмо, пуш) шага истории не имеет: там возврат к корню раздела заменой
   *  адреса, иначе «назад» вынесло бы человека из кабинета вовсе. */
  const pushedRef = useRef(false);
  // ТИХАЯ ПОДМЕНА АДРЕСА, НЕ НАВИГАЦИЯ NEXT (владелец 09.09.2026, разбор по журналу сборки
  // 173: после смены языка адрес стал /en/…, и настоящая навигация Next на подстраницу
  // «Аккаунта» видела ДРУГОЙ корневой сегмент [locale] — собирала дерево кабинета заново,
  // закрывая и открывая все родные экраны; между ними мигал веб). Подстраницы «Аккаунта»
  // ходят тем же способом, что вкладки стека (shell.tsx navTo): history.pushState — Next
  // синхронизирует usePathname, дерево не трогает. Это общее правило кабинета, не обход.
  const openNativePage = (key: string) => {
    pushedRef.current = true;
    window.history.pushState(null, '', pickToPath('account', key));
  };
  const openSection = (s: SettingsSection) => {
    if (s.flip) return;
    // «Справка» — своей рукой (в браузере — слоем с адресом /app/account/help; расширили окно до
    // трёх слоёв — слой уезжает в правое поле тем же адресом, useSheetToPaneOnWide).
    if (s.key === 'help' || s.key === 'privacy' || s.key === 'terms') {
      if (!isNativeApp()) sheetOpenedRef.current = 'help';
      return s.key === 'help' ? openHelp() : openLegal(s.key);
    }
    // Родную страницу открывает натив сам — вебу остаётся адрес (см. openNativePage).
    if (nativeAccountSupported() && NATIVE_PAGE_KEYS.has(s.key)) return openNativePage(s.key);
    sheetOpenedRef.current = s.key;
    if (s.key === 'profile') return openProfile();
    openPage(s);
  };
  // ПРЯМОЙ ЗАХОД ПО АДРЕСУ на узком виде (телефон): /app/account/subscription из письма
  // или пуша обязан открыть саму страницу, а не список раздела — вся воронка ведёт
  // клиента именно этой ссылкой. Открываем ту же шторку, что и клик по строке, ОДИН раз
  // на адрес. В приложении «Подписки» в списке нет вовсе — там ничего и не откроется
  // (CLAUDE.md п.7: платного в сторах не показываем).
  const deepOpened = useRef<string | null>(null);
  useEffect(() => {
    if (wide || !pick || deepOpened.current === pick) return;
    // КАПКАН ШИРИНЫ (владелец 2026-08-10, четвёртый скрин): `wide` приходит пропсом от
    // useSplitWide, а тот первым кадром отдаёт false — при ПЕРЕЗАГРУЗКЕ страницы на
    // широком экране этот эффект успевал открыть плавающую панель, и раздел показывался
    // дважды: третьим слоем в колонке и панелью поверх. Ширину меряем прямо здесь, как
    // это делает оболочка для быстрой настройки (shell.tsx).
    // ДВА СЛОЯ (821–1023) — КАК ТЕЛЕФОН (ТЗ WEB-CAUSES М75, проба 06.10: /app/account/help на 960 и
    // перезагрузка открытой справки показывали голый корень «Аккаунта»): колонки и поля там нет,
    // страница открывается тем же слоем, что и по клику. Отрезаем только три слоя — там её рисует поле.
    if (readLayoutMode() === 'three') return;
    // КАПКАН: признак «мы в приложении» приходит на кадр позже (живёт в эффекте), и на
    // первом кадре список ещё СОДЕРЖИТ «Подписку» — без этой проверки прямой заход по
    // адресу успевал открыть оплату внутри приложения. Ждём, пока признак устоится.
    if (isNativeApp() !== native) return;
    // РОДНАЯ СТРАНИЦА вместо шторки (этап Б): в свежей сборке «Тариф», «Язык и регион» и
    // «Безопасность» рисует натив, и веб-копия поверх неё не открывается — ни по клику,
    // ни прямым заходом по адресу.
    if (nativeAccountSupported() && NATIVE_PAGE_KEYS.has(pick)) return;
    const s = sections.find((x) => x.key === pick);
    if (!s) return;
    deepOpened.current = pick;
    // ОТКРЫТИЕ — ПОСЛЕ ЭФФЕКТА, микрозадачей (О11, 07.10.2026): drawer.open коммитит уровень
    // через flushSync, а из эффекта React его не сливает и пишет в консоль «flushSync was
    // called from inside a lifecycle method» (значок Next «1 Issue» на «Подписке» при 402).
    // Микрозадача выполняется до отрисовки кадра — шторка встаёт тем же кадром, что и раньше.
    queueMicrotask(() => openSection(s));
  });
  // «Настройки» шторкой — одной рукой для веб-строки и родного экрана (accountTap).
  const openSettingsSheet = () =>
    drawer.open({
      title: '',
      body: (
        <>
          <SettingsHero
            icon={<GearIcon />}
            title={t('sections.settings')}
            meta={t('settings.settingsMeta')}
            color={SETTINGS_COLOR}
          />
          <SettingsPanel />
        </>
      ),
    });
  // ── РОДНОЙ РАЗДЕЛ «АККАУНТ» (docs/NATIVE-ACCOUNT-PLAN.md): в приложении со свежей
  // сборкой раздел рисует натив (стекло iOS 26), а этот компонент остаётся мозгом и
  // запаской — открывает прежние шторки по accountTap и ведёт адрес. Сами данные собирает
  // ОТДЕЛЬНЫЙ водитель ниже: вне приложения он не монтируется вовсе, и его запросы
  // (сводка тарифа, пояс, безопасность) веб-страницу не трогают.
  //
  // КАПКАН ГИДРАЦИИ (инцидент 355a478f): ветвить разметку по nativeMethod() напрямую
  // нельзя — сервер рисует веб-вариант, клиент в приложении родной, React пересобирает
  // страницу с нуля. Поэтому признак берём монтируемыми хуками (useNativeApp +
  // useNativeMethod), и водитель появляется вторым кадром.
  const hasAccountBridge = useNativeMethod('accountOpen');
  // Два слоя (821–1023): «Настройки» — свой пункт левого меню, строка в поле — дубль (пакет Д2.2 п.7).
  const layoutMode = useLayoutMode();
  const nativeDriver =
    native && hasAccountBridge ? (
      <AccountNativeDriver
        user={user}
        sections={sections}
        stack={stack}
        settingsTitle={t('sections.settings')}
        onRow={(key) => {
          // «Справка» из корня «Аккаунта» (15.09.2026): страницы у натива нет — он лишь
          // сообщил тап; открываем родные экраны справки листом-стопкой НАД корнем.
          // Уровней это не прибавляет: в стопку страниц раздела справка не входит.
          if (key === 'help') return openHelp();
          if (key === 'privacy' || key === 'terms') return openLegal(key);
          // «Сервер»: страницы нет — тап переключает приложение на другой сервер.
          if (key === 'server') return toggleNativeServer();
          // Родная страница уже выехала у натива — вебу остаётся дописать адрес. Герой
          // раздела ведёт туда же: «Профиль» с этапа Г — тоже родная страница (фото,
          // имя, контакты; внизу «Выйти»), а не прежняя веб-шторка.
          if (NATIVE_PAGE_KEYS.has(key)) return openNativePage(key);
          // «Настройки» и их простые страницы — родные, но без адреса (веб-шторка его
          // тоже не меняла): веб только запоминает стопку. Подстраницы «Уведомлений» и «Профиля»
          // (волна 3) живут по тому же правилу — в вебе это боковые шторки без адреса.
          if (
            isNativeSettingsKey(key) ||
            NATIVE_NOTIFY_PAGE_KEYS.has(key) ||
            NATIVE_PROFILE_PAGE_KEYS.has(key)
          ) {
            return pushNativeSettings(key);
          }
          if (key === 'profile') return openProfile();
          const s = sections.find((x) => x.key === key);
          if (s) openSection(s);
        }}
        onBack={(key) => {
          // тоже не меняла): веб только запоминает стопку. Подстраницы «Уведомлений» и «Профиля»
          // (волна 3) живут по тому же правилу — в вебе это боковые шторки без адреса.
          if (
            isNativeSettingsKey(key) ||
            NATIVE_NOTIFY_PAGE_KEYS.has(key) ||
            NATIVE_PROFILE_PAGE_KEYS.has(key)
          ) {
            return popNativeSettings(key);
          }
          if (pushedRef.current) {
            pushedRef.current = false;
            router.back();
          } else {
            // Тихая замена адреса — по той же причине, что openNativePage выше.
            window.history.replaceState(null, '', pickToPath('account', ''));
          }
        }}
      />
    ) : null;
  // Форма корня «Аккаунта» на телефоне (ред. 9, 7.4.2): строки раздела — снять при показе (своих данных-списка у корня
  // нет, заглушки не нужно; форма — для обхода и сверки).
  useShapeRecord('account', !wide && !(native && hasAccountBridge));
  if (wide) {
    // Корень раздела — «Профиль» (владелец 2026-08-10): нажали «Аккаунт» — открылся он.
    const p = sections.find((x) => x.key === (pick || 'profile'));
    if (p) {
      return (
        <>
          {nativeDriver}
          {/* ТРИ СЛОЯ (ТЗ WEB-CAUSES М50, владелец 05.10 21:21): кружок темы живёт ТОЛЬКО в герое
              колонки «Аккаунт» (split-menu.tsx), в шапке поля его нет ни на одной странице;
              колокольчик — ТОЛЬКО на странице «Уведомления» (он и есть её угловая кнопка, второй
              не ставим). Слева в шапке поля остаётся лишь «‹» уровней «Справки». На телефоне и в
              двух слоях — прежняя шапка экрана (shell.tsx). */}
          <PaneHead
            title={p.key === 'partner' ? <PartnerPaneTitle root={p.title} /> : p.title}
            lead={p.key === 'help' ? <HelpPaneLead root={null} /> : p.key === 'partner' ? <PartnerPaneLead /> : undefined}
          >
            {/* Пилюли баланса токенов в шапке профиля больше нет: вход в историю токенов
                спрятан до пополнений (пополнение токенов позже, владелец 29.08). Кнопку
                страницы, если она у страницы есть, рисует общий слот ниже. */}
            {p.action || p.key === 'help' || p.key === 'notifications' ? (
              <PageAction>
                {p.action ? p.action.dom : null}
              </PageAction>
            ) : null}
            {/* «Партнёрам» в поле — списки шагом вглубь по адресу (PAY-FLOW-TZ §17.3 п.4). */}
            {p.key === 'help' ? <HelpPane /> : p.key === 'partner' ? <PartnerPanel pane /> : p.body}
          </PaneHead>
        </>
      );
    }
    return (
      <>
        {nativeDriver}
        <ProfileCard user={user} />
      </>
    );
  }
  const hero = <ProfileCard user={user} />;
  const settingsRow = {
    // Розовая плитка вместо чёрной (владелец 29.08.2026); под белый глиф — насыщенный
    // --accent-stroke (пастель #feb3c8 плиткой нечитаема, журнал 29.08).
    color: SETTINGS_COLOR,
    icon: <GearIcon />,
    title: t('sections.settings'),
    // Узкий вид: «Настройки» — ШТОРКА поверх (канон «нет колонки, всё шторкой»);
    // на широком этого пункта тут нет вовсе — он отдельным пунктом рейла.
    // Шторка открывается КАК У СОСЕДЕЙ свода (владелец 29.08.2026): титул пуст, сверху
    // герой страницы (значок, название, короткая подпись) — см. useOpenSection. На
    // широком экране страница живёт в правом поле под PaneHead, и героя там нет.
    onClick: openSettingsSheet,
  };
  // УЗКИЙ ВИД — ЧТО УБРАНО ИЗ СПИСКА (владелец 12.08.2026, Э7.1):
  //  • «Профиль» — дубль: над списком стоит сам герой профиля, и тап по фото и по
  //    подписи открывает тот же редактор (ProfileCard → useOpenProfile);
  //  • «Тема» и «Язык» — переехали в шапку (тема — кружком слева, AccountThemeButton).
  // Пузырь, оставшийся совсем без строк (в приложении блок 1 — это один «Профиль»:
  // подписка, партнёрка и заработок там скрыты), не рисуется: группы SectionRows
  // считает по факту. В колонке широкого вида список остаётся прежним — капсулы там нет.
  // «Справка» на узком вебе — вторым пузырём ПОД «Настройками» (владелец 15.09.2026):
  // из свода уходит, встаёт рядом с settingsRow (в приложении она группой 4 под группой 3).
  const rows = sections.filter(
    (s) => s.key !== 'profile' && s.key !== 'help' && s.key !== 'privacy' && s.key !== 'terms' && !s.flip,
  );
  const help = sections.find((s) => s.key === 'help');
  const helpRow = help
    ? { color: help.color, icon: help.icon, title: help.title, onClick: () => openSection(help) }
    : null;
  // Юр-строки — одним пузырём под «Справкой» (та же пара, что в приложении).
  const legalRows = sections
    .filter((s) => s.key === 'privacy' || s.key === 'terms')
    .map((s) => ({ color: s.color, icon: s.icon, title: s.title, onClick: () => openSection(s) }));
  // Экран рисует натив — вебу тут рисовать НЕЧЕГО (владелец 01.09.2026:
  // «перещёлкивается веб» — веб-версия мелькала при слайде ухода с вкладки и под
  // шторкой профиля своим крупным героем): остаётся один водитель-мозг без разметки.
  // Признак — монтируемыми хуками (см. капкан гидрации выше), не при рендере.
  if (native && hasAccountBridge) return nativeDriver;
  return (
    <>
      {nativeDriver}
      {/* Веб-вид раздела; в приложении с родным «Аккаунтом» скрыт классом native-account
          (native-tabs-init.ts) — под родным экраном веб не мелькает (владелец 30.09). */}
      <div className="acc-web" data-shape="account">
        {/* ЗАКОН 4 НА ДВУХ СЛОЯХ (пакет Д2.2 п.7, кадр audit-960/X-account): строки «Настройки» нет —
            «Настройки» стоят пунктом левого меню, как в трёх слоях; на телефоне строка остаётся. */}
        <SectionRows
          sections={rows}
          open={openSection}
          before={hero}
          after={[...(layoutMode === 'two' ? [] : [settingsRow]), ...(helpRow ? [helpRow] : []), ...(legalRows.length ? [legalRows] : [])]}
        />
      </div>
    </>
  );
}

/** ВОДИТЕЛЬ РОДНОГО РАЗДЕЛА: собирает данные корня и подстраниц и шлёт их нативу. Живёт
 *  отдельным компонентом, потому что монтируется ТОЛЬКО в приложении со свежей сборкой —
 *  вне его ни один из этих запросов (сводка тарифа, часовой пояс, безопасность) не нужен
 *  и веб-странице ничего не стоит. Своей разметки у водителя нет. */
function AccountNativeDriver({
  user,
  sections,
  stack,
  settingsTitle,
  onRow,
  onBack,
}: {
  user: ProfileInfo;
  sections: SettingsSection[];
  stack: string[];
  settingsTitle: string;
  onRow: (key: string, page: string) => void;
  onBack: (key: string) => void;
}) {
  const t = useTranslations('cabinet.settings');
  const [themeAttr] = useThemeAttr();
  // РОДНАЯ СПРАВКА (владелец 10.09.2026) живёт на этом же водителе: строка «Справка»
  // стоит в своде «Настроек», а стопку её экранов ведёт свой хук (сборка без метода
  // helpOpen — молчит, и справка открывается прежним листом с веб-видом).
  const openLegal = useOpenLegal();
  useNativeHelp(themeAttr, openLegal);
  // null — фото героя ещё едет: водитель ждёт и не шлёт первый кадр без него (иначе
  // видимый скачок «буква → фото», запись экрана владельца 01.09.2026).
  const [heroPhoto, setHeroPhoto] = useState<string | null>(null);
  const { pages, applySet, tapRow, onPhoto } = useNativeAccountPages(
    sections,
    stack,
    user,
    heroPhoto ?? '',
    settingsTitle,
  );
  useEffect(() => {
    let dead = false;
    // То же фото, что у вкладки меню (avatarTabPng), но крупнее — герой, а не значок.
    const push = async () => {
      const data = (await avatarTabPng(user.avatarTag, 320)) ?? '';
      if (!dead) setHeroPhoto(data);
    };
    void push();
    const onChanged = () => void push();
    window.addEventListener(AVATAR_CHANGED_EVENT, onChanged);
    return () => {
      dead = true;
      window.removeEventListener(AVATAR_CHANGED_EVENT, onChanged);
    };
  }, [user.avatarTag, user.hasAvatar]);
  useNativeAccount({
    ready: heroPhoto !== null,
    pages,
    stack,
    hero: {
      name: user.name,
      email: user.email,
      sub: user.phone ? `Telegram · ${user.phone}` : (user.tgName ?? ''),
      photo: heroPhoto ?? '',
      initial: user.initial,
    },
    rows: [
      ...sections
        .filter((s) => s.key !== 'profile' && !s.flip)
        .map((s) => ({ key: s.key, title: s.title, color: s.color, group: s.group, meta: s.meta })),
      // Подпись строки нужна ГЕРОЮ её страницы (общий механизм героя достраивает его по
      // строке свода с тем же ключом) — в самой строке списка её нет.
      { key: 'settings', title: settingsTitle, color: SETTINGS_COLOR, group: 3, meta: t('settingsMeta') },
    ],
    theme: themeAttr,
    // Строка-кнопка подстраницы разбирается ЕЮ ЖЕ (шторка «Полученных», список
    // «Сообщений», «Тихие часы», «Выйти»); не её ключ — уходит своду раздела.
    onRow: (key, page) => {
      // Страницу ВИДА (карточка человека, доступа, форма) натив уже открыл сам — вебу
      // остаётся налить в неё содержимое (это делает tapRow) и ЗАПОМНИТЬ стопку.
      if (!tapRow(key) || page) onRow(page || key, page);
    },
    onSet: applySet,
    onBack,
    onPhoto,
  });
  return null;
}

// AccountHero (фото с пилюлей токенов слева и кружком профиля справа) удалён
// 2026-08-10: корень «Аккаунта» на широком экране — САМА страница профиля, второго
// фото над ней не бывает. Пилюля токенов переехала в шапку этой страницы, кружок
// профиля не нужен вовсе — профиль уже открыт. На узком виде герой остался прежним
// (ProfileCard), обе кнопки там рисует шапка кабинета.

// «Партнёрам» — своим файлом: partner-panel.tsx (PAY-FLOW-TZ §17.3).

// ─────────────────────────── Модалка: Подключить ИИ-ассистента (MCP) ───────────────────────────
interface McpInfo {
  ready: boolean;
  mcpUrl: string;
  token: string;
  tokenUrl: string;
}

// Приложения, подключённые через OAuth («жми да», MCP-PUBLIC-PLAN 3.1).
// Строка списка = ОДНО подключение (семья токенов ≈ устройство), владелец 2026-07-26:
// слитые строки «Claude ×2» не давали понять, что это за устройства.
interface McpApp {
  clientId: string;
  familyId: string;
  name: string;
  userAgent: string | null;
  connectedAt: string; // ISO
  lastSeenAt: string; // ISO — последняя ротация refresh ≈ последняя активность
}

/** Короткая человеческая метка устройства из user-agent браузера согласия:
 *  «iPhone · Safari», «Mac · Chrome». Ничего не распознали — null (строка без метки). */
function deviceLabel(ua: string | null): string | null {
  if (!ua) return null;
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS X|Macintosh/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : null;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\/|CriOS\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : null;
  if (!os && !browser) return null;
  return [os, browser].filter(Boolean).join(' · ');
}

// Официальный знак Claude (simple-icons, фирменный #e07a68) — «изображение, чтоб было
// весело» на шторке подключения (владелец 2026-07-23).
const CLAUDE_PATH =
  'm4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z';
const ClaudeMark = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d={CLAUDE_PATH} />
  </svg>
);

// Знак Cursor (simple-icons, моно — красим в цвет текста).
const CURSOR_PATH =
  'M11.503.131 1.891 5.678a.84.84 0 0 0-.42.726v11.188c0 .3.162.575.42.724l9.609 5.55a1 1 0 0 0 .998 0l9.61-5.55a.84.84 0 0 0 .42-.724V6.404a.84.84 0 0 0-.42-.726L12.497.131a1.01 1.01 0 0 0-.996 0M2.657 6.338h18.55c.263 0 .43.287.297.515L12.23 22.918c-.062.107-.229.064-.229-.06V12.335a.59.59 0 0 0-.295-.51l-9.11-5.257c-.109-.063-.064-.23.061-.23';
// Знак OpenAI (ChatGPT/Codex) — готовый ассет n8n из /public (в simple-icons его нет),
// тёмная тема — свой файл (useIsDark, как у иконок шагов).
const OPENAI_ICON_LIGHT =
  '/service-icons/_n8n__n8n-nodes-langchain__dist__nodes__embeddings__EmbeddingsOpenAI__openAiLight.svg';
const OPENAI_ICON_DARK =
  '/service-icons/_n8n__n8n-nodes-langchain__dist__nodes__embeddings__EmbeddingsOpenAI__openAiLight.dark.svg';

// Логотип подключённого приложения по имени (владелец 2026-07-23: «иконки подключённых
// программ — их считанное количество»): Claude / OpenAI (ChatGPT, Codex) / Cursor,
// прочим — общий значок MCP серым. Без рамок-подложек, просто знак.
function McpAppMark({ name }: { name: string }) {
  const dark = useIsDark();
  if (/claude/i.test(name)) {
    return (
      <span className="mcp-app-mark" style={{ color: '#e07a68' }}>
        <ClaudeMark />
      </span>
    );
  }
  if (/chatgpt|openai|codex|gpt/i.test(name)) {
    return (
      <span className="mcp-app-mark">
        <N8nIconImg src={dark ? OPENAI_ICON_DARK : OPENAI_ICON_LIGHT} size={24} />
      </span>
    );
  }
  if (/cursor/i.test(name)) {
    return (
      <span className="mcp-app-mark" style={{ color: 'var(--ink)' }}>
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d={CURSOR_PATH} />
        </svg>
      </span>
    );
  }
  return (
    <span className="mcp-app-mark" style={{ color: 'var(--mut)' }}>
      <McpIcon />
    </span>
  );
}

// Иконка «отключить» (питание) — вместо текстовой кнопки (владелец 2026-07-23).
const PowerIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);

// Главный экран (владелец 2026-07-23): презентация раздела сверху, ниже подключённые
// приложения СПИСКОМ + строка «Подключить приложение» — как «Сотрудники». Пустой
// список — только строка-плюс, без пояснительного текста.
/** ПОДКЛЮЧЁННЫЕ ИИ-ПРИЛОЖЕНИЯ — ОДНОЙ РУКОЙ: список, разрыв ОДНОГО подключения
 *  (семьи-устройства) и шторка «Подключить приложение». Ею живут веб-страница и РОДНАЯ
 *  страница «MCP» — второй правды о подключениях нет. `open=false` — страницу сейчас не
 *  смотрят: кэш держим, в сеть не идём (пустой адрес пропускает запрос). */
function useMcpApps(open = true): {
  apps: McpApp[];
  loading: boolean;
  busy: string | null;
  disconnect: (clientId: string, familyId: string) => void;
  openConnect: () => void;
} {
  const t = useTranslations('cabinet.settings');
  const panel = useSlidePanel();
  const [busy, setBusy] = useState<string | null>(null);
  const { data, loading, reload } = usePersistentData<{ apps: McpApp[] }>(
    'cabinet:mcp-apps',
    open ? '/api/cabinet/mcp/apps' : '',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );

  // Рвём ОДНО подключение (семью-устройство), не все подключения приложения разом.
  const disconnect = (clientId: string, familyId: string) => {
    setBusy(familyId);
    void (async () => {
      try {
        await fetch('/api/cabinet/mcp/apps', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientId, familyId }),
        });
        reload();
      } finally {
        setBusy(null);
      }
    })();
  };

  // «Подключить» — ВЕРТИКАЛЬНАЯ шторка поверх (владелец 2026-07-23): в приложении —
  // нижний лист стопкой над боковым слоем, на сайте — шторка снизу поверх модалки.
  const openConnect = () =>
    panel.open({ title: t('mcpConnectRow'), sheet: true, body: <McpConnectSheet /> });

  return { apps: data?.apps ?? [], loading, busy, disconnect, openConnect };
}

function McpAppsPanel({ inline = false }: { inline?: boolean }) {
  const t = useTranslations('cabinet.settings');
  const locale = useLocale();
  const { apps, loading, busy, disconnect, openConnect } = useMcpApps();

  return (
    <DGroup>
      {/* Своего «хиро по центру» у страницы БОЛЬШЕ НЕТ (владелец 2026-08-07): на
          развороте страница живёт общим видом — заголовок даёт <PaneHead>, что это
          такое, объясняет обычная серая подпись, действие — кнопка В СТРОКЕ ЗАГОЛОВКА.
          В шторке узкого вида заголовок по-прежнему рисует герой открывалки. */}
      <DHint>{t('mcpIntro')}</DHint>
      {inline ? (
        <PageAction>
          <PageAddButton label={t('mcpConnectRow')} onClick={openConnect} />
        </PageAction>
      ) : (
        // Узкий вид (шторка): прежняя кнопка над списком — так же, как у «Сотрудников».
        !loading && (
          <KBareButton className="k-btn--action cta-grad" onClick={openConnect}>
            {t('mcpConnectRow')}
          </KBareButton>
        )
      )}
      {loading ? (
        <Sk w={260} h={16} />
      ) : (
        // Заголовок группы — НАД пузырём (владелец 15.08.2026), его ставит сам DList.
        <DList label={t('mcpAppsHead')}>
          {apps.length === 0 ? (
            <DEmpty>{t('mcpAppsEmpty')}</DEmpty>
          ) : (
          apps.map((app) => {
            const dev = deviceLabel(app.userAgent);
            const seen = new Date(app.lastSeenAt).toLocaleDateString(locale);
            const connected = new Date(app.connectedAt).toLocaleDateString(locale);
            return (
            <div className="row" key={app.familyId}>
              <McpAppMark name={app.name} />
              <div className="row-mid grow" style={{ minWidth: 0 }}>
                {/* Имя + устройство подключения (владелец 2026-07-26: два «Claude» без
                    примет неразличимы); ниже — когда подключено и последняя активность. */}
                <div className="nm">{dev ? `${app.name} · ${dev}` : app.name}</div>
                <div className="meta">
                  {t('mcpAppConnectedAt', { date: connected })}
                  {seen !== connected ? ` · ${t('mcpAppSeenAt', { date: seen })}` : ''}
                </div>
              </div>
              <div className="right">
                {/* Отключение рвёт доступ ИИ-приложения — подтверждаем инлайн (Ф29);
                    триггер — иконка питания, не текст (владелец 2026-07-23). */}
                <ConfirmInline
                  label={<PowerIcon />}
                  question={t('mcpAppDisconnectQ')}
                  confirmLabel={t('mcpAppDisconnect')}
                  triggerClassName="rowicon"
                  triggerTitle={t('mcpAppDisconnect')}
                  busy={busy === app.familyId}
                  onConfirm={() => disconnect(app.clientId, app.familyId)}
                />
              </div>
            </div>
            );
          })
          )}
        </DList>
      )}
    </DGroup>
  );
}

// Поле «значение + иконка-копия»: обычный инпут кабинета (та же высота, что везде на
// сайте; владелец 2026-07-23: «низкие input у нас не используются») и кнопка со значком
// копирования вместо слова. Это ПОЛЕ ВНУТРИ пузыря (FIn: свой заголовок + контрол +
// подсказка), а не отдельный пузырь: пузырь тут — способ подключения (FBlock ниже).
function McpCopyField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const t = useTranslations('cabinet.settings');
  return (
    <FIn label={label} hint={hint}>
      <div className="copyfld">
        <KInput type="text" readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
        <CopyBtn value={value} label={t('copy')} />
      </div>
    </FIn>
  );
}

// Шторка «Подключить приложение» (вертикальная, владелец 2026-07-23): БЕЗ повторной
// маркетинговой шапки — строго ДВА способа: через вход (для приложений) и по ключу
// (для консолей). Запасной токен-URL выпилен.
// Вид — набором формы (15.08.2026): способ = ПУЗЫРЬ с названием над ним (FBlock), «для
// кого» — описание под пузырём, поля внутри со своими заголовками. Прежняя своя карточка
// `.card mcp-way` держала внутри ещё по пузырю на каждое поле (пузырь в пузыре) и своё
// поле 20 — канон «Элементы формы» такого не знает.
function McpConnectSheet() {
  const t = useTranslations('cabinet.settings');
  // sensitive: токен MCP — секрет, живёт только in-memory (не localStorage).
  const { data: info, loading } = usePersistentData<McpInfo>('cabinet:mcp', '/api/cabinet/mcp', {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
    sensitive: true,
  });

  if (loading) return <Sk w={260} h={16} />;
  if (!info) return <div className="meta">{t('mcpUnavailable')}</div>;

  return (
    <FPane>
      {!info.ready && <div className="keybanner">{t('mcpNotReady')}</div>}
      <FBlock label={t('mcpWay1Title')} hint={t('mcpWay1Sub')}>
        <McpCopyField label={t('mcpUrlLabel')} value={info.mcpUrl} hint={t('mcpOauthHint')} />
      </FBlock>
      {/* «По ключу» — и адрес, и ключ разом (владелец 2026-07-23): под каждым полем
          серым подписано, куда его вставлять. */}
      <FBlock label={t('mcpWay2Title')} hint={t('mcpWay2Sub')}>
        <McpCopyField label={t('mcpUrlLabel')} value={info.mcpUrl} hint={t('mcpKeyUrlHint')} />
        <McpCopyField label={t('mcpTokenLabel')} value={info.token} hint={t('mcpKeyHeaderHint')} />
      </FBlock>
    </FPane>
  );
}
